import React, { useState, useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { renderTemplate } from '../utils/templateRenderer';
import { db, auth, storage } from '../firebase/firebaseConfig';
import { doc, getDoc, setDoc, updateDoc, serverTimestamp, collection, addDoc } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { getParsedResumeData } from '../services/resumeService';
import { loadTemplateHTML } from '../services/templateService';
import { customToast } from '../utils/toastUtils';
import { ServerExportService } from '../services/serverExportService';
import { sanitizeTemplateHTML } from '../utils/templateSanitizer';
import { filterResumeData, canExportResume, getResumeDataSummary } from '../utils/resumeDataFilter';
import { generateFilename } from '../utils/pdfExporter';
import FeedbackModal from '../components/FeedbackModal';
import PhotoUploadModal from '../components/PhotoUploadModal';
import LimitExceededModal from '../components/LimitExceededModal';
import ProfessionalSummaryGenerator from '../components/ProfessionalSummaryGenerator';
import useAddonPurchase from '../hooks/useAddonPurchase';
import ResumePreview from '../components/ResumePreview';
import {
  COLOR_PALETTES,
  deriveThemeVariables,
  injectThemeIntoHTML,
  getTierColorPolicy,
} from '../utils/colorThemeEngine';
import { useUser } from '../contexts/UserContext';
import { saveResumeEditorState, restoreResumeEditorState, clearResumeEditorState } from '../utils/editorStatePreserver';
import './ResumeEditorV2.css';

const ResumeEditorV2 = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { userData } = useUser();
  const { purchaseAddon, isPurchasing } = useAddonPurchase();
  const sidebarTimeoutRef = useRef(null);
  const colorBtnRef = useRef(null);
  const fontBtnRef = useRef(null);
  const skillsInputRef = useRef(null);
  const skillsDropdownRef = useRef(null);
  const sectionToggleRef = useRef(null);
  const colorDropdownRef = useRef(null);
  const fontDropdownRef = useRef(null);

  // Get selected template from navigation state (or will be restored from session)
  const [selectedTemplate, setSelectedTemplate] = useState(location.state?.selectedTemplate || null);
  const parsedDataId = location.state?.parsedDataId; // Get parsed data ID if coming from import
  const addonResumeData = location.state?.addonResumeData || null; // Pre-fill data from addon browse silver flow
  const restoredFromSessionRef = useRef(false);
  const redirectCheckedRef = useRef(false); // Ensures redirect logic only runs once

  // Editing state (coming from MyFiles "Edit Resume")
  const isEditing = location.state?.isEditing || false;
  const editResumeId = location.state?.editResumeId || null;
  const editResumeData = location.state?.editResumeData || null;
  const editTemplateHTML = location.state?.editTemplateHTML || null;
  const editColorTheme = location.state?.editColorTheme || '';
  const editFont = location.state?.editFont || 'Calibri';
  const editHiddenSections = location.state?.editHiddenSections || [];

  // Sidebar state
  const [sidebarExpanded, setSidebarExpanded] = useState(true);
  const [sidebarHovered, setSidebarHovered] = useState(false);

  // Mobile view toggle: 'editor' or 'preview'
  const [mobileView, setMobileView] = useState('editor');

  // Step navigation
  const [currentStep, setCurrentStep] = useState(0);

  // Theme settings
  const [colorTheme, setColorTheme] = useState('blue');
  const [fontFamily, setFontFamily] = useState('Calibri');
  const [showColorPicker, setShowColorPicker] = useState(false);
  const [showFontPicker, setShowFontPicker] = useState(false);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0 });
  const [colorVars, setColorVars] = useState(null);  // Real-time CSS variable map for iframe
  const [fontFocusIdx, setFontFocusIdx] = useState(-1);
  const fontListRef = useRef(null);

  // Save Draft state
  const [isSavingDraft, setIsSavingDraft] = useState(false);
  const idleTimerRef = useRef(null);
  const isSavingRef = useRef(false);
  const resumeDataRef = useRef(null); // Will be synced with resumeData via useEffect
  const handleSaveDraftRef = useRef(null); // Keep latest handleSaveDraft accessible to auto-save timer

  // Skills autocomplete state
  const [skillInput, setSkillInput] = useState('');
  const [showSkillsDropdown, setShowSkillsDropdown] = useState(false);
  const [filteredSkills, setFilteredSkills] = useState([]);
  const [highlightedSkillIndex, setHighlightedSkillIndex] = useState(-1);
  const [softSkillInput, setSoftSkillInput] = useState('');
  const [showSoftSkillsDropdown, setShowSoftSkillsDropdown] = useState(false);
  const [filteredSoftSkills, setFilteredSoftSkills] = useState([]);
  const [highlightedSoftSkillIndex, setHighlightedSoftSkillIndex] = useState(-1);
  const [editingSkillIndex, setEditingSkillIndex] = useState(null);
  const [editingSoftSkillIndex, setEditingSoftSkillIndex] = useState(null);

  // Export state
  const [exportProgress, setExportProgress] = useState(0);
  const [exportStatus, setExportStatus] = useState('');
  const [showExportDialog, setShowExportDialog] = useState(false);
  const [showFeedbackModal, setShowFeedbackModal] = useState(false);
  const [resumeId, setResumeId] = useState(null);

  // HR Consultancy checkbox state
  const [saveToHRConsultancy, setSaveToHRConsultancy] = useState(false);

  // Addon modal state
  const [showAddonModal, setShowAddonModal] = useState(false);
  
  // Photo upload state
  const [showPhotoModal, setShowPhotoModal] = useState(false);
  const [photoPreview, setPhotoPreview] = useState('');

  // Section visibility state
  const [hiddenSections, setHiddenSections] = useState([]);
  const [showSectionToggle, setShowSectionToggle] = useState(false);

  // Template rendering
  const [templateHTML, setTemplateHTML] = useState('');
  const [previewHTML, setPreviewHTML] = useState('');
  const [loading, setLoading] = useState(true);

  // Inline form states (replacing modals)
  const [showExperienceForm, setShowExperienceForm] = useState(false);
  const [showEducationForm, setShowEducationForm] = useState(false);
  const [showProjectForm, setShowProjectForm] = useState(false);
  const [showCertificationForm, setShowCertificationForm] = useState(false);
  const [showLanguageForm, setShowLanguageForm] = useState(false);
  const [editingIndex, setEditingIndex] = useState(null);

  // Form data for inline forms
  const [currentExperience, setCurrentExperience] = useState({
    jobTitle: '',
    company: '',
    experienceLocation: '',
    startDate: '',
    endDate: '',
    currentlyWorking: false,
    responsibilities: ''
  });

  const [currentEducation, setCurrentEducation] = useState({
    degree: '',
    institution: '',
    educationLocation: '',
    graduationDate: '',
    gpa: '',
    fieldOfStudy: ''
  });

  const [currentProject, setCurrentProject] = useState({
    projectName: '',
    projectDescription: '',
    technologies: '',
    projectDate: '',
    projectLink: ''
  });

  const [currentCertification, setCurrentCertification] = useState({
    certificationName: '',
    issuingOrganization: '',
    certificationDate: '',
    credentialId: ''
  });

  const [currentLanguage, setCurrentLanguage] = useState({
    language: '',
    proficiency: 'Professional Working'
  });

  // Form data state
  const [resumeData, setResumeData] = useState({
    // Personal Information
    fullName: '',
    title: '',
    email: '',
    phone: '',
    location: '',
    linkedin: '',
    github: '',
    portfolio: '',
    website: '',
    
    // Summary
    summary: '',
    
    // Experience
    experiences: [],
    
    // Education
    education: [],
    
    // Skills
    technicalSkills: [],
    softSkills: [],
    languages: [],
    tools: [],
    
    // Projects
    projects: [],
    
    // Certifications
    certifications: [],
    
    // Photo
    photoURL: '',
    photoDataUri: ''
  });

  // Steps configuration
  const steps = [
    { 
      id: 'personal', 
      label: 'Personal Info', 
      icon: '👤',
      color: '#3b82f6'
    },
    { 
      id: 'summary', 
      label: 'Professional Summary', 
      icon: '📝',
      color: '#8b5cf6'
    },
    { 
      id: 'experience', 
      label: 'Work Experience', 
      icon: '💼',
      color: '#06b6d4'
    },
    { 
      id: 'education', 
      label: 'Education', 
      icon: '🎓',
      color: '#10b981'
    },
    { 
      id: 'skills', 
      label: 'Skills', 
      icon: '⚡',
      color: '#f59e0b'
    },
    { 
      id: 'projects', 
      label: 'Projects', 
      icon: '🚀',
      color: '#ec4899'
    },
    { 
      id: 'certifications', 
      label: 'Certifications', 
      icon: '🏆',
      color: '#14b8a6'
    },
    { 
      id: 'preview', 
      label: 'Final Preview', 
      icon: '👁️',
      color: '#6366f1'
    }
  ];

  // Color palettes — single source of truth from colorThemeEngine
  const colorPalettes = COLOR_PALETTES;

  // Swatch gradient lookup (for UI display only)
  const swatchGradients = {
    teal:    ['#0f766e', '#14b8a6'],
    blue:    ['#1d4ed8', '#3b82f6'],
    purple:  ['#7c3aed', '#a855f7'],
    indigo:  ['#4338ca', '#6366f1'],
    rose:    ['#be123c', '#f43f5e'],
    emerald: ['#047857', '#10b981'],
    cyan:    ['#0891b2', '#22d3ee'],
    amber:   ['#d97706', '#f59e0b'],
    orange:  ['#ea580c', '#f97316'],
    pink:    ['#db2777', '#ec4899'],
    red:     ['#b91c1c', '#ef4444'],
    green:   ['#15803d', '#22c55e'],
    slate:   ['#334155', '#475569'],
    charcoal:['#0f172a', '#1e293b'],
  };

  // Tier color policy — also enforce based on user's current plan
  const templateTier = (selectedTemplate?.tier || '').toLowerCase();
  const userPlan = (userData?.planType || 'free').toLowerCase();
  const templatePolicy = getTierColorPolicy(templateTier);
  // Silver users can never change colors, even on gold/platinum templates they edited before
  const tierColorPolicy = userPlan === 'silver'
    ? { enabled: false, label: 'Color themes are not available for Silver plan users. Upgrade to Gold or Platinum to customize colors.' }
    : templatePolicy;

  // Font families
  const fontFamilies = [
    'Arial', 'Calibri', 'Helvetica', 'Verdana', 
    'Times New Roman', 'Georgia', 'Lato', 'Roboto', 'Garamond',
    'Open Sans', 'Montserrat', 'Raleway', 'Nunito', 'Poppins'
  ];

  // Maps each font to its Google Fonts stylesheet URL (null = system font, no import needed)
  const GOOGLE_FONT_URLS = {
    'Lato':       'https://fonts.googleapis.com/css2?family=Lato:ital,wght@0,300;0,400;0,700;1,400&display=swap',
    'Roboto':     'https://fonts.googleapis.com/css2?family=Roboto:ital,wght@0,300;0,400;0,700;1,400&display=swap',
    'Garamond':   'https://fonts.googleapis.com/css2?family=EB+Garamond:ital,wght@0,400;0,700;1,400&display=swap',
    'Open Sans':  'https://fonts.googleapis.com/css2?family=Open+Sans:ital,wght@0,300;0,400;0,700;1,400&display=swap',
    'Montserrat': 'https://fonts.googleapis.com/css2?family=Montserrat:ital,wght@0,300;0,400;0,700;1,400&display=swap',
    'Raleway':    'https://fonts.googleapis.com/css2?family=Raleway:ital,wght@0,300;0,400;0,700;1,400&display=swap',
    'Nunito':     'https://fonts.googleapis.com/css2?family=Nunito:ital,wght@0,300;0,400;0,700;1,400&display=swap',
    'Poppins':    'https://fonts.googleapis.com/css2?family=Poppins:ital,wght@0,300;0,400;0,700;1,400&display=swap',
  };

  // CSS font-family value with proper fallbacks per font (handles server-side rendering)
  const FONT_CSS_FAMILIES = {
    'Arial':           "Arial, Helvetica, sans-serif",
    'Calibri':         "Calibri, Carlito, 'Trebuchet MS', sans-serif",
    'Helvetica':       "Helvetica, Arial, sans-serif",
    'Verdana':         "Verdana, Geneva, sans-serif",
    'Times New Roman': "'Times New Roman', Times, serif",
    'Georgia':         "Georgia, 'Times New Roman', serif",
    'Lato':            "'Lato', sans-serif",
    'Roboto':          "'Roboto', sans-serif",
    'Garamond':        "'EB Garamond', Garamond, Georgia, serif",
    'Open Sans':       "'Open Sans', sans-serif",
    'Montserrat':      "'Montserrat', sans-serif",
    'Raleway':         "'Raleway', sans-serif",
    'Nunito':          "'Nunito', sans-serif",
    'Poppins':         "'Poppins', sans-serif",
  };

  /**
   * Injects Google Font <link> and font-family CSS into an HTML string.
   * This ensures correct rendering both in the browser preview AND in
   * Puppeteer (server-side PDF), which cannot rely on browser cache.
   */
  const injectFontIntoHTML = (html, font) => {
    const googleFontURL = GOOGLE_FONT_URLS[font];
    const cssFontFamily = FONT_CSS_FAMILIES[font] || `'${font}', sans-serif`;

    let result = html;

    // 1. Inject Google Font <link> into <head> so Puppeteer downloads it
    if (googleFontURL) {
      const fontLink = `<link rel="preconnect" href="https://fonts.googleapis.com">` +
        `<link rel="preconnect" href="https://fonts.gstatic.com" crossorigin>` +
        `<link rel="stylesheet" href="${googleFontURL}">`;
      result = result.replace('</head>', `${fontLink}\n</head>`);
    }

    // 2. Override font-family at the start of every <style> block
    result = result.replace(/<style>/g, `<style>* { font-family: ${cssFontFamily} !important; }\n`);
    // Also handle <style type="text/css"> variants
    result = result.replace(/<style\s+type="text\/css">/g, `<style type="text/css">* { font-family: ${cssFontFamily} !important; }\n`);

    return result;
  };

  // Auto-hide sidebar after 3 seconds
  useEffect(() => {
    sidebarTimeoutRef.current = setTimeout(() => {
      setSidebarExpanded(false);
    }, 3000);

    return () => {
      if (sidebarTimeoutRef.current) {
        clearTimeout(sidebarTimeoutRef.current);
      }
    };
  }, []);

  // On mount: decide whether to stay on editor or redirect to templates.
  // This runs ONCE. If returning from pricing, restore state and show toast.
  // If no template and not editing, redirect to templates gallery.
  useEffect(() => {
    if (redirectCheckedRef.current) return;
    redirectCheckedRef.current = true;

    // Case 1: Template already set (normal navigation from gallery) — do nothing
    if (selectedTemplate || isEditing) return;

    // Case 2: Returning from pricing page after payment
    const isReturningFromPayment = location.state?.triggerDownload;

    if (isReturningFromPayment) {
      // Try restoring from sessionStorage first
      const savedState = restoreResumeEditorState();
      if (savedState?.selectedTemplate) {
        restoredFromSessionRef.current = true;
        setSelectedTemplate(savedState.selectedTemplate);
        if (savedState.resumeData) setResumeData(prev => ({ ...prev, ...savedState.resumeData }));
        if (savedState.templateHTML) setTemplateHTML(savedState.templateHTML);
        if (savedState.colorTheme) setColorTheme(savedState.colorTheme);
        if (savedState.fontFamily) setFontFamily(savedState.fontFamily);
        if (savedState.hiddenSections) setHiddenSections(savedState.hiddenSections);
        if (savedState.currentStep !== undefined) setCurrentStep(savedState.currentStep);
        if (savedState.resumeId) setResumeId(savedState.resumeId);
        // Photo: restore from resumeData.photoURL (base64 not saved to sessionStorage)
        if (savedState.resumeData?.photoURL) setPhotoPreview(savedState.resumeData.photoURL);
        setLoading(false);
        clearResumeEditorState();

        // CRITICAL: Render preview immediately using the restored data.
        // The regular useEffect [resumeData, templateHTML, ...] may not fire
        // immediately due to React batching. Do it explicitly here.
        if (savedState.templateHTML && savedState.resumeData) {
          try {
            let rendered = renderTemplate(savedState.templateHTML, savedState.resumeData, { hiddenSections: savedState.hiddenSections || [] });
            // Inject color theme if available
            const palette = COLOR_PALETTES[savedState.colorTheme];
            if (palette) {
              rendered = injectThemeIntoHTML(rendered, palette.primary);
            }
            rendered = injectFontIntoHTML(rendered, savedState.fontFamily || 'Calibri');
            setPreviewHTML(rendered);
          } catch (e) {
            console.warn('Preview render on return failed:', e);
          }
        }
      } else if (location.state?.resumeId) {
        // Fallback: load draft from Firestore
        const user = auth.currentUser;
        if (user) {
          const resumeRef = doc(db, 'users', user.uid, 'resumes', location.state.resumeId);
          getDoc(resumeRef).then(resumeSnap => {
            if (resumeSnap.exists()) {
              const draft = resumeSnap.data();
              restoredFromSessionRef.current = true;
              if (draft.data) setResumeData(prev => ({ ...prev, ...draft.data }));
              if (draft.templateHTML) setTemplateHTML(draft.templateHTML);
              if (draft.templateColorTheme) setColorTheme(draft.templateColorTheme);
              if (draft.selectedFont) setFontFamily(draft.selectedFont);
              if (draft.hiddenSections) setHiddenSections(draft.hiddenSections);
              setResumeId(location.state.resumeId);
              setSelectedTemplate({
                id: draft.templateId || location.state.resumeId,
                name: draft.templateName || 'Resume',
                plan: draft.templateTier || 'free',
                tier: draft.templateTier || 'free',
              });
              setCurrentStep(7);
            }
            setLoading(false);
          }).catch(() => setLoading(false));
        } else {
          setLoading(false);
        }
      } else {
        // No sessionStorage and no resumeId — still stay on editor, don't redirect
        setLoading(false);
      }

      // Show toast notification (once)
      const msg = location.state?.message;
      if (msg) {
        setTimeout(() => customToast.success(msg, { autoClose: 6000 }), 500);
      }
      setTimeout(() => {
        customToast.info('Click "Download PDF" when you\'re ready to export.', { autoClose: 8000 });
      }, 2500);

      // Clear navigation state so refreshing doesn't re-trigger
      window.history.replaceState({}, document.title);
      return;
    }

    // Case 3: No template, not editing, not returning from payment → redirect to gallery
    navigate('/templates');
  }, []);

  // Load editing data if coming from MyFiles "Edit Resume"
  useEffect(() => {
    if (isEditing && editResumeData) {
      // Ensure ALL array fields have safe defaults even if the saved data doesn't contain them
      setResumeData({
        fullName: '', title: '', email: '', phone: '', location: '',
        linkedin: '', github: '', portfolio: '', website: '',
        summary: '',
        experiences: [],
        education: [],
        technicalSkills: [],
        softSkills: [],
        tools: [],
        languages: [],
        projects: [],
        certifications: [],
        photoURL: '', photoDataUri: '',
        ...editResumeData
      });
      setResumeId(editResumeId);
      if (editColorTheme) setColorTheme(editColorTheme);
      if (editFont) setFontFamily(editFont);
      if (editHiddenSections?.length > 0) setHiddenSections(editHiddenSections);

      // Check if editTemplateHTML is a genuine raw mustache template (has {{placeholders}})
      // OR if it's rendered HTML that was incorrectly saved (no mustache markers)
      const isRawMustacheTemplate = editTemplateHTML && editTemplateHTML.includes('{{');

      // Helper: load the original raw mustache template from the templates collection
      // using templateId (e.g. "gold-45" → templates/gold/resume-gold-45.html)
      const loadRawTemplateByTemplateId = () => {
        const templateId = selectedTemplate?.id || null;
        if (templateId && templateId.includes('-')) {
          const [tier, ...rest] = templateId.split('-');
          const number = rest.join('-'); // Handle cases like "free-1"
          const templatePath = `templates/${tier}/resume-${tier}-${number}.html`;

          loadTemplateHTML(templatePath)
            .then(html => {
              setTemplateHTML(html);
              setLoading(false);
            })
            .catch(err => {
              console.error('❌ Failed to load raw template by templateId:', err);
              // Last resort: use editTemplateHTML even if it's rendered (at least shows content)
              if (editTemplateHTML) {
                setTemplateHTML(editTemplateHTML);
              }
              setLoading(false);
            });
        } else {
          // No valid templateId — use whatever we have
          if (editTemplateHTML) {
            setTemplateHTML(editTemplateHTML);
          } else {
            console.error('❌ No template source available for editing');
          }
          setLoading(false);
        }
      };

      if (isRawMustacheTemplate) {
        // editTemplateHTML is a genuine raw template with {{placeholders}} — use it directly
        setTemplateHTML(editTemplateHTML);
        setLoading(false);
      } else {
        // editTemplateHTML is either missing or rendered HTML — load raw template
        loadRawTemplateByTemplateId();
      }

      customToast.success('Resume loaded for editing! ✏️');
    }
  }, [isEditing]);

  // Pre-fill resume data when coming from "Browse Silver Templates" addon flow
  useEffect(() => {
    if (addonResumeData && !isEditing) {
      setResumeData(prev => ({ ...prev, certifications: [], projects: [], languages: [], ...addonResumeData }));
    }
  }, [addonResumeData]);

  // Fetch and load template HTML (for new resumes only — editing mode handles its own template loading)
  useEffect(() => {
    if (isEditing) return; // Editing mode loads template in its own useEffect above
    // Skip fetch if state was restored from sessionStorage (template HTML already set)
    if (restoredFromSessionRef.current) {
      restoredFromSessionRef.current = false;
      return;
    }
    if (selectedTemplate && !editTemplateHTML) {
      setLoading(true);
      // Use loadTemplateHTML which knows how to use Vite's raw glob loader
      // in production (templates are bundled as JS modules, not static files)
      const templatePath = selectedTemplate.storagePath || 
        `templates/${selectedTemplate.plan || selectedTemplate.tier}/${selectedTemplate.filename || `resume-${selectedTemplate.plan || selectedTemplate.tier}-${selectedTemplate.number || selectedTemplate.id?.split('-').pop()}.html`}`;
      loadTemplateHTML(templatePath)
        .then(html => {
          setTemplateHTML(html);
          setLoading(false);
        })
        .catch(err => {
          console.error('Error loading template:', err);
          // Fallback: try direct fetch (works if templates are served as static files)
          if (selectedTemplate.downloadURL) {
            fetch(selectedTemplate.downloadURL)
              .then(res => {
                if (!res.ok) throw new Error(`HTTP ${res.status}`);
                return res.text();
              })
              .then(html => {
                // Strip any Vite-injected CSS that bleeds app styles
                const cleanHtml = html.replace(/<link\s+[^>]*href="\/assets\/[^"]*\.css"[^>]*>/gi, '');
                setTemplateHTML(cleanHtml);
                setLoading(false);
              })
              .catch(err2 => {
                console.error('Fallback fetch also failed:', err2);
                setLoading(false);
              });
          } else {
            setLoading(false);
          }
        });
    }
  }, [selectedTemplate]);

  // Load parsed resume data if parsedDataId is provided (from resume import)
  useEffect(() => {
    const loadParsedData = async () => {
      if (!parsedDataId) return;

      try {
        const parsedResume = await getParsedResumeData(parsedDataId);
        
        if (!parsedResume || !parsedResume.parsedData) {
          customToast.error('Failed to load imported resume data');
          return;
        }

        const parsed = parsedResume.parsedData;

        // Helper: convert parser date format (MM/YYYY) to display format (Mon YYYY)
        const MONTH_NAMES = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
                             'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
        const formatParserDate = (dateStr) => {
          if (!dateStr) return '';
          const s = dateStr.trim();
          if (/^present$/i.test(s)) return 'Present';
          // MM/YYYY → "Mon YYYY"
          const mmYYYY = s.match(/^(\d{1,2})\/(\d{4})$/);
          if (mmYYYY) {
            const monthIdx = parseInt(mmYYYY[1], 10) - 1;
            if (monthIdx >= 0 && monthIdx < 12) return `${MONTH_NAMES[monthIdx]} ${mmYYYY[2]}`;
            return mmYYYY[2]; // fallback to year only
          }
          // YYYY-MM → "Mon YYYY"
          const yyyyMM = s.match(/^(\d{4})-(\d{1,2})$/);
          if (yyyyMM) {
            const monthIdx = parseInt(yyyyMM[2], 10) - 1;
            if (monthIdx >= 0 && monthIdx < 12) return `${MONTH_NAMES[monthIdx]} ${yyyyMM[1]}`;
            return yyyyMM[1];
          }
          return s; // already formatted or unrecognized — return as-is
        };

        // FIX 1: Extract title from first experience position (templates show {{title}} under name)
        const extractedTitle = (parsed.experience && parsed.experience.length > 0)
          ? (parsed.experience[0].position || '')
          : '';

        // Known programming languages / frameworks / tools for reclassification
        const PROG_SKILL_SET = new Set([
          'javascript', 'typescript', 'python', 'java', 'c++', 'c#', 'c', 'ruby', 'go', 'golang',
          'rust', 'swift', 'kotlin', 'php', 'perl', 'scala', 'r', 'matlab', 'dart', 'lua',
          'haskell', 'elixir', 'clojure', 'erlang', 'f#', 'objective-c', 'assembly',
          'sql', 'nosql', 'mysql', 'postgresql', 'mongodb', 'redis', 'elasticsearch',
          'html', 'html5', 'css', 'css3', 'sass', 'scss', 'tailwind', 'bootstrap',
          'graphql', 'rest', 'restful', 'grpc', 'bash', 'shell', 'powershell',
          'react', 'reactjs', 'react.js', 'react native', 'angular', 'vue', 'vuejs',
          'svelte', 'next.js', 'nextjs', 'nuxt', 'gatsby',
          'node', 'nodejs', 'node.js', 'express', 'nestjs', 'fastify', 'koa',
          'django', 'flask', 'fastapi', 'spring', 'spring boot', 'rails', 'laravel',
          '.net', 'asp.net', 'blazor', 'tensorflow', 'pytorch', 'keras', 'pandas', 'numpy',
          'redux', 'mobx', 'zustand', 'mongoose', 'sequelize', 'prisma', 'typeorm',
          'jest', 'mocha', 'cypress', 'selenium', 'playwright', 'puppeteer',
          'flutter', 'swiftui', 'jetpack compose',
          'git', 'github', 'docker', 'kubernetes', 'jenkins', 'aws', 'azure', 'gcp',
          'firebase', 'heroku', 'netlify', 'vercel', 'webpack', 'vite',
          'figma', 'sketch', 'photoshop', 'illustrator', 'jira', 'postman',
          'terraform', 'ansible', 'kafka', 'rabbitmq', 'nginx',
          'machine learning', 'deep learning', 'data science', 'microservices', 'serverless',
        ]);
        const isProgrammingSkill = (name) => {
          const lower = (name || '').toLowerCase().trim();
          if (PROG_SKILL_SET.has(lower)) return true;
          if (PROG_SKILL_SET.has(lower.replace(/[.\s]/g, ''))) return true;
          for (const kw of PROG_SKILL_SET) {
            if (lower.startsWith(kw + ' ') || lower === kw) return true;
          }
          return false;
        };

        // Separate spoken languages from misrouted programming skills in parsed.languages
        const spokenLanguages = [];
        const misroutedSkills = [];
        for (const lang of (parsed.languages || [])) {
          const name = typeof lang === 'string' ? lang : (lang.language || lang.name || '');
          if (isProgrammingSkill(name)) {
            misroutedSkills.push(name);
          } else {
            spokenLanguages.push(lang);
          }
        }
        if (misroutedSkills.length > 0) {
        }

        // Merge all technical skills: skills.technical + skills.languages + misrouted from languages section
        const mergedTechnicalSkills = [
          ...(parsed.skills?.technical || []),
          ...(parsed.skills?.languages || []),
          ...misroutedSkills
        ];

        // Transform parsed data to editor format
        const transformedData = {
          // Personal Information
          fullName: parsed.personalInfo?.fullName || '',
          title: extractedTitle,
          email: parsed.personalInfo?.email || '',
          phone: parsed.personalInfo?.phone || '',
          location: parsed.personalInfo?.location || '',
          linkedin: parsed.personalInfo?.linkedin || '',
          github: parsed.personalInfo?.github || '',
          portfolio: parsed.personalInfo?.portfolio || '',
          website: parsed.personalInfo?.portfolio || parsed.personalInfo?.github || '',
          
          // Summary
          summary: parsed.summary || '',
          
          // Experience
          experiences: (parsed.experience || []).map((exp, index) => ({
            id: Date.now() + index,
            jobTitle: exp.position || '',
            company: exp.company || '',
            experienceLocation: exp.location || '',
            startDate: formatParserDate(exp.startDate),
            endDate: formatParserDate(exp.endDate),
            currentlyWorking: /^present$/i.test((exp.endDate || '').trim()),
            responsibilities: exp.description || ''
          })),
          
          // Education
          education: (parsed.education || []).map((edu, index) => ({
            id: Date.now() + index,
            degree: edu.degree || '',
            institution: edu.institution || '',
            educationLocation: edu.location || '',
            graduationDate: formatParserDate(edu.graduationDate),
            gpa: edu.gpa || '',
            fieldOfStudy: edu.fieldOfStudy || edu.achievements || ''
          })),
          
          // Skills (merged: skills.technical + skills.languages + reclassified from languages section)
          technicalSkills: mergedTechnicalSkills,
          softSkills: parsed.skills?.soft || [],
          // Only actual spoken languages (programming skills filtered out above)
          languages: spokenLanguages.map(lang => {
            if (typeof lang === 'string') return { language: lang, proficiency: '' };
            return {
              language: lang.language || lang.name || '',
              proficiency: lang.proficiency || lang.level || ''
            };
          }),
          tools: parsed.skills?.tools || [],
          
          // Projects
          projects: (parsed.projects || []).map((proj, index) => ({
            id: Date.now() + index,
            projectName: proj.name || '',
            projectDescription: proj.description || '',
            technologies: Array.isArray(proj.technologies) ? proj.technologies.join(', ') : proj.technologies || '',
            projectLink: proj.link || '',
            projectDate: formatParserDate(proj.date) || ''
          })),
          
          // Certifications (FIX 5: use certificationIssuer to match template {{certificationIssuer}})
          certifications: (parsed.certifications || []).map((cert, index) => ({
            id: Date.now() + index,
            certificationName: cert.name || '',
            certificationIssuer: cert.issuer || cert.issuingOrganization || '',
            issuingOrganization: cert.issuer || cert.issuingOrganization || '',
            certificationDate: formatParserDate(cert.date),
            expiryDate: formatParserDate(cert.expiryDate) || ''
          })),
          
          // Photo
          photoURL: '',
          photoDataUri: ''
        };

        // Set the resume data
        setResumeData(prev => ({
          ...prev,
          ...transformedData
        }));
        
        customToast.success('Resume data imported successfully! 🎉');
        
      } catch (error) {
        console.error('❌ Error loading parsed resume:', error);
        customToast.error('Failed to load imported resume: ' + error.message);
      }
    };

    loadParsedData();
  }, [parsedDataId]);

  // Render preview when resumeData, templateHTML, or theme changes
  // Produces the SAME HTML sent to the PDF backend — no extra wrappers
  useEffect(() => {
    if (templateHTML) {
      try {
        // Render template with resume data (with hidden sections applied)
        let rendered = renderTemplate(templateHTML, resumeData, { hiddenSections });
        
        // Inject CSS variable-based color theme (replaces old Mustache substitution)
        const palette = colorPalettes[colorTheme];
        if (palette && tierColorPolicy.enabled) {
          rendered = injectThemeIntoHTML(rendered, palette.primary);
        }
        
        // Apply font family (injects Google Font import + CSS override)
        rendered = injectFontIntoHTML(rendered, fontFamily);
        
        // Set preview — ResumePreview component handles iframe injection,
        // A4 sizing, multi-page detection, zoom, and shadow overlays
        setPreviewHTML(rendered);
      } catch (err) {
        console.error('Error rendering template:', err);
        setPreviewHTML('<html><body><p style="padding: 20px; text-align: center; color: #999;">Error rendering template preview</p></body></html>');
      }
    }
  }, [resumeData, templateHTML, colorTheme, fontFamily, tierColorPolicy.enabled, hiddenSections]);

  // Sidebar mouse handlers
  const handleSidebarMouseEnter = () => {
    setSidebarHovered(true);
    setSidebarExpanded(true);
  };

  const handleSidebarMouseLeave = () => {
    setSidebarHovered(false);
    setSidebarExpanded(false);
  };

  // Theme picker handlers
  const handleColorPickerToggle = () => {
    if (colorBtnRef.current) {
      const rect = colorBtnRef.current.getBoundingClientRect();
      const dropdownWidth = 300;
      const dropdownHeight = 400;
      
      // Calculate position, ensuring it stays within viewport
      let top = rect.bottom + 8;
      let left = rect.left;
      
      // Adjust if dropdown would go off right edge
      if (left + dropdownWidth > window.innerWidth) {
        left = window.innerWidth - dropdownWidth - 20;
      }
      
      // Adjust if dropdown would go off bottom edge
      if (top + dropdownHeight > window.innerHeight) {
        top = rect.top - dropdownHeight - 8;
      }
      
      setDropdownPosition({ top, left });
    }
    setShowColorPicker(!showColorPicker);
    setShowFontPicker(false);
    setShowSectionToggle(false);
  };

  // Keyboard navigation for font picker
  const handleFontKeyDown = (e) => {
    const len = fontFamilies.length;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setFontFocusIdx(i => Math.min(i + 1, len - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setFontFocusIdx(i => Math.max(i - 1, 0));
    } else if (e.key === 'Escape') {
      setShowFontPicker(false);
    }
  };

  // Focus the highlighted font button whenever fontFocusIdx changes
  useEffect(() => {
    if (showFontPicker && fontFocusIdx >= 0 && fontListRef.current) {
      const btns = fontListRef.current.querySelectorAll('button');
      if (btns[fontFocusIdx]) btns[fontFocusIdx].focus();
    }
  }, [fontFocusIdx, showFontPicker]);

  // When font picker opens, focus the currently selected font
  useEffect(() => {
    if (showFontPicker) {
      const idx = fontFamilies.indexOf(fontFamily);
      setFontFocusIdx(idx >= 0 ? idx : 0);
    } else {
      setFontFocusIdx(-1);
    }
  }, [showFontPicker]);

  const handleFontPickerToggle = () => {
    if (fontBtnRef.current) {
      const rect = fontBtnRef.current.getBoundingClientRect();
      const dropdownWidth = 300;
      const dropdownHeight = 450;
      
      // Calculate position, ensuring it stays within viewport
      let top = rect.bottom + 8;
      let left = rect.left;
      
      // Adjust if dropdown would go off right edge
      if (left + dropdownWidth > window.innerWidth) {
        left = window.innerWidth - dropdownWidth - 20;
      }
      
      // Adjust if dropdown would go off bottom edge
      if (top + dropdownHeight > window.innerHeight) {
        top = rect.top - dropdownHeight - 8;
      }
      
      setDropdownPosition({ top, left });
    }
    setShowFontPicker(!showFontPicker);
    setShowColorPicker(false);
    setShowSectionToggle(false);
  };

  const handleColorChange = (color) => {
    setColorTheme(color);
    setShowColorPicker(false);

    // Push real-time CSS variable update to the iframe (no full re-render)
    const palette = colorPalettes[color];
    if (palette && tierColorPolicy.enabled) {
      setColorVars(deriveThemeVariables(palette.primary));
    }
  };

  const handleFontChange = (font) => {
    setFontFamily(font);
    setShowFontPicker(false);
  };

  // Scroll the inline editing form into view after it renders
  const scrollToInlineForm = () => {
    setTimeout(() => {
      const form = document.querySelector('.rev2-inline-add-form');
      if (form) {
        form.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }, 100);
  };

  // Inline form handlers - Experience
  const openExperienceForm = (index = null) => {
    if (index !== null) {
      setCurrentExperience(resumeData.experiences[index]);
      setEditingIndex(index);
    } else {
      setCurrentExperience({
        jobTitle: '',
        company: '',
        experienceLocation: '',
        startDate: '',
        endDate: '',
        currentlyWorking: false,
        responsibilities: ''
      });
      setEditingIndex(null);
    }
    setShowExperienceForm(true);
    scrollToInlineForm();
  };

  const closeExperienceForm = () => {
    setShowExperienceForm(false);
    setEditingIndex(null);
  };

  const saveExperience = () => {
    // Validate end date is filled unless currently working
    if (!currentExperience.currentlyWorking && !currentExperience.endDate?.trim()) {
      customToast.warning('Please enter an End Date, or check "I currently work here" if this is your current job.');
      return;
    }

    if (editingIndex !== null) {
      const updated = [...resumeData.experiences];
      updated[editingIndex] = currentExperience;
      setResumeData({ ...resumeData, experiences: updated });
    } else {
      setResumeData({
        ...resumeData,
        experiences: [...resumeData.experiences, currentExperience]
      });
    }
    closeExperienceForm();
  };

  const deleteExperience = (index) => {
    const updated = resumeData.experiences.filter((_, i) => i !== index);
    setResumeData({ ...resumeData, experiences: updated });
    // Close form if editing the deleted item or if index is stale
    if (showExperienceForm && (editingIndex === index || editingIndex >= updated.length)) {
      closeExperienceForm();
    }
  };

  // Inline form handlers - Education
  const openEducationForm = (index = null) => {
    if (index !== null) {
      setCurrentEducation(resumeData.education[index]);
      setEditingIndex(index);
    } else {
      setCurrentEducation({
        degree: '',
        institution: '',
        educationLocation: '',
        graduationDate: '',
        gpa: '',
        fieldOfStudy: ''
      });
      setEditingIndex(null);
    }
    setShowEducationForm(true);
    scrollToInlineForm();
  };

  const closeEducationForm = () => {
    setShowEducationForm(false);
    setEditingIndex(null);
  };

  const saveEducation = () => {
    if (editingIndex !== null) {
      const updated = [...resumeData.education];
      updated[editingIndex] = currentEducation;
      setResumeData({ ...resumeData, education: updated });
    } else {
      setResumeData({
        ...resumeData,
        education: [...resumeData.education, currentEducation]
      });
    }
    closeEducationForm();
  };

  const deleteEducation = (index) => {
    const updated = resumeData.education.filter((_, i) => i !== index);
    setResumeData({ ...resumeData, education: updated });
  };

  // Predefined Skills Database (150+ skills)
  const predefinedTechnicalSkills = [
    // Programming Languages
    'JavaScript', 'TypeScript', 'Python', 'Java', 'C++', 'C#', 'Ruby', 'PHP', 'Swift', 'Kotlin',
    'Go', 'Rust', 'Scala', 'R', 'MATLAB', 'Perl', 'Dart', 'Objective-C', 'Shell Scripting', 'PowerShell',
    
    // Frontend
    'React', 'Angular', 'Vue.js', 'Next.js', 'Nuxt.js', 'Svelte', 'jQuery', 'Bootstrap', 'Tailwind CSS',
    'Material-UI', 'Sass', 'LESS', 'HTML5', 'CSS3', 'Webpack', 'Vite', 'Redux', 'MobX', 'Zustand',
    
    // Backend
    'Node.js', 'Express.js', 'Django', 'Flask', 'FastAPI', 'Spring Boot', 'ASP.NET', 'Ruby on Rails',
    'Laravel', 'Symfony', 'NestJS', 'GraphQL', 'REST API', 'gRPC', 'Microservices',
    
    // Databases
    'MySQL', 'PostgreSQL', 'MongoDB', 'Redis', 'Cassandra', 'Oracle', 'SQL Server', 'SQLite',
    'DynamoDB', 'Firebase', 'Elasticsearch', 'Neo4j', 'CouchDB', 'MariaDB',
    
    // Cloud & DevOps
    'AWS', 'Azure', 'Google Cloud', 'Docker', 'Kubernetes', 'Jenkins', 'GitLab CI', 'GitHub Actions',
    'CircleCI', 'Travis CI', 'Terraform', 'Ansible', 'Chef', 'Puppet', 'Serverless',
    
    // Mobile
    'React Native', 'Flutter', 'iOS Development', 'Android Development', 'Xamarin', 'Ionic',
    
    // Testing
    'Jest', 'Mocha', 'Chai', 'Cypress', 'Selenium', 'JUnit', 'PyTest', 'TestNG', 'Postman',
    
    // Tools & Others
    'Git', 'GitHub', 'GitLab', 'Bitbucket', 'JIRA', 'Confluence', 'Slack', 'VS Code', 'IntelliJ IDEA',
    'Eclipse', 'Nginx', 'Apache', 'Linux', 'Unix', 'Windows Server', 'Mac OS',
    
    // Data Science & AI
    'Machine Learning', 'Deep Learning', 'TensorFlow', 'PyTorch', 'Keras', 'Scikit-learn',
    'Pandas', 'NumPy', 'Data Analysis', 'Data Visualization', 'Tableau', 'Power BI', 'Apache Spark',
    
    // Other Technical
    'Agile', 'Scrum', 'Kanban', 'CI/CD', 'Version Control', 'Unit Testing', 'Integration Testing',
    'Performance Optimization', 'Security', 'OAuth', 'JWT', 'WebSockets', 'Progressive Web Apps',
    'Responsive Design', 'Cross-browser Compatibility', 'Accessibility', 'SEO', 'Web Performance'
  ];

  const predefinedSoftSkills = [
    'Communication', 'Leadership', 'Teamwork', 'Problem Solving', 'Critical Thinking',
    'Time Management', 'Adaptability', 'Creativity', 'Work Ethic', 'Attention to Detail',
    'Decision Making', 'Conflict Resolution', 'Emotional Intelligence', 'Interpersonal Skills',
    'Project Management', 'Public Speaking', 'Presentation Skills', 'Negotiation', 'Active Listening',
    'Mentoring', 'Coaching', 'Analytical Thinking', 'Strategic Planning', 'Collaboration',
    'Customer Service', 'Sales Skills', 'Marketing Skills', 'Research Skills', 'Writing Skills',
    'Planning', 'Organization', 'Multitasking', 'Flexibility', 'Self-Motivation', 'Initiative',
    'Stress Management', 'Risk Management', 'Innovation', 'Change Management', 'Client Relations'
  ];

  // Skills autocomplete handlers
  const handleSkillInputChange = (e) => {
    const value = e.target.value;
    setSkillInput(value);
    setHighlightedSkillIndex(-1);
    
    if (value.trim().length > 0) {
      const filtered = predefinedTechnicalSkills.filter(skill =>
        skill.toLowerCase().includes(value.toLowerCase())
      ).slice(0, 10); // Show max 10 suggestions
      setFilteredSkills(filtered);
      setShowSkillsDropdown(true);
    } else {
      setShowSkillsDropdown(false);
    }
  };

  const handleSoftSkillInputChange = (e) => {
    const value = e.target.value;
    setSoftSkillInput(value);
    setHighlightedSoftSkillIndex(-1);
    
    if (value.trim().length > 0) {
      const filtered = predefinedSoftSkills.filter(skill =>
        skill.toLowerCase().includes(value.toLowerCase())
      ).slice(0, 10);
      setFilteredSoftSkills(filtered);
      setShowSoftSkillsDropdown(true);
    } else {
      setShowSoftSkillsDropdown(false);
    }
  };

  const addTechnicalSkill = (skill) => {
    if (!(resumeData.technicalSkills || []).includes(skill)) {
      setResumeData({
        ...resumeData,
        technicalSkills: [...(resumeData.technicalSkills || []), skill]
      });
    }
    setSkillInput('');
    setShowSkillsDropdown(false);
  };

  const addSoftSkill = (skill) => {
    if (!(resumeData.softSkills || []).includes(skill)) {
      setResumeData({
        ...resumeData,
        softSkills: [...(resumeData.softSkills || []), skill]
      });
    }
    setSoftSkillInput('');
    setShowSoftSkillsDropdown(false);
  };

  const removeTechnicalSkill = (skillToRemove) => {
    setResumeData({
      ...resumeData,
      technicalSkills: (resumeData.technicalSkills || []).filter(s => s !== skillToRemove)
    });
  };

  const removeSoftSkill = (skillToRemove) => {
    setResumeData({
      ...resumeData,
      softSkills: (resumeData.softSkills || []).filter(s => s !== skillToRemove)
    });
  };

  const handleSkillKeyPress = (e) => {
    if (showSkillsDropdown && filteredSkills.length > 0) {
      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault();
          setHighlightedSkillIndex(prev => prev < filteredSkills.length - 1 ? prev + 1 : 0);
          return;
        case 'ArrowUp':
          e.preventDefault();
          setHighlightedSkillIndex(prev => prev > 0 ? prev - 1 : filteredSkills.length - 1);
          return;
        case 'Enter':
          e.preventDefault();
          if (highlightedSkillIndex >= 0 && highlightedSkillIndex < filteredSkills.length) {
            addTechnicalSkill(filteredSkills[highlightedSkillIndex]);
          } else if (skillInput.trim()) {
            addTechnicalSkill(skillInput.trim());
          }
          return;
        case 'Escape':
          e.preventDefault();
          setShowSkillsDropdown(false);
          setHighlightedSkillIndex(-1);
          return;
        default:
          break;
      }
    }
    if (e.key === 'Enter' && skillInput.trim()) {
      e.preventDefault();
      addTechnicalSkill(skillInput.trim());
    }
  };

  const handleSoftSkillKeyPress = (e) => {
    if (showSoftSkillsDropdown && filteredSoftSkills.length > 0) {
      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault();
          setHighlightedSoftSkillIndex(prev => prev < filteredSoftSkills.length - 1 ? prev + 1 : 0);
          return;
        case 'ArrowUp':
          e.preventDefault();
          setHighlightedSoftSkillIndex(prev => prev > 0 ? prev - 1 : filteredSoftSkills.length - 1);
          return;
        case 'Enter':
          e.preventDefault();
          if (highlightedSoftSkillIndex >= 0 && highlightedSoftSkillIndex < filteredSoftSkills.length) {
            addSoftSkill(filteredSoftSkills[highlightedSoftSkillIndex]);
          } else if (softSkillInput.trim()) {
            addSoftSkill(softSkillInput.trim());
          }
          return;
        case 'Escape':
          e.preventDefault();
          setShowSoftSkillsDropdown(false);
          setHighlightedSoftSkillIndex(-1);
          return;
        default:
          break;
      }
    }
    if (e.key === 'Enter' && softSkillInput.trim()) {
      e.preventDefault();
      addSoftSkill(softSkillInput.trim());
    }
  };

  // Edit skill handlers
  const startEditTechnicalSkill = (index) => {
    setEditingSkillIndex(index);
    setSkillInput(resumeData.technicalSkills[index]);
  };

  const startEditSoftSkill = (index) => {
    setEditingSoftSkillIndex(index);
    setSoftSkillInput(resumeData.softSkills[index]);
  };

  const confirmEditTechnicalSkill = () => {
    if (editingSkillIndex !== null && skillInput.trim()) {
      const updated = [...resumeData.technicalSkills];
      updated[editingSkillIndex] = skillInput.trim();
      setResumeData({ ...resumeData, technicalSkills: updated });
      setEditingSkillIndex(null);
      setSkillInput('');
    }
  };

  const confirmEditSoftSkill = () => {
    if (editingSoftSkillIndex !== null && softSkillInput.trim()) {
      const updated = [...resumeData.softSkills];
      updated[editingSoftSkillIndex] = softSkillInput.trim();
      setResumeData({ ...resumeData, softSkills: updated });
      setEditingSoftSkillIndex(null);
      setSoftSkillInput('');
    }
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (skillsDropdownRef.current && !skillsDropdownRef.current.contains(event.target) &&
          skillsInputRef.current && !skillsInputRef.current.contains(event.target)) {
        setShowSkillsDropdown(false);
        setShowSoftSkillsDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Close color picker, font picker, and section toggle when clicking outside
  useEffect(() => {
    const handleGlobalClick = (event) => {
      // Close section toggle panel if click is outside
      if (showSectionToggle && sectionToggleRef.current && !sectionToggleRef.current.contains(event.target)) {
        setShowSectionToggle(false);
      }
      // Close color picker if click is outside
      if (showColorPicker && colorDropdownRef.current && !colorDropdownRef.current.contains(event.target) &&
          colorBtnRef.current && !colorBtnRef.current.contains(event.target)) {
        setShowColorPicker(false);
      }
      // Close font picker if click is outside
      if (showFontPicker && fontDropdownRef.current && !fontDropdownRef.current.contains(event.target) &&
          fontBtnRef.current && !fontBtnRef.current.contains(event.target)) {
        setShowFontPicker(false);
      }
    };

    document.addEventListener('mousedown', handleGlobalClick);
    return () => document.removeEventListener('mousedown', handleGlobalClick);
  }, [showSectionToggle, showColorPicker, showFontPicker]);

  // Save Draft Handler
  const handleSaveDraft = async ({ silent = false } = {}) => {
    // Validate personal information before saving
    const name = resumeData.fullName?.trim() || '';
    if (!name) {
      if (!silent) {
        customToast.warning('Kindly fill out at least personal information to save your draft');
        setCurrentStep(0);
      }
      return;
    }

    if (isSavingRef.current) return; // Prevent concurrent saves
    isSavingRef.current = true;

    try {
      setIsSavingDraft(true);
      
      const user = auth.currentUser;
      if (!user) {
        if (!silent) {
          customToast.warning('Please login to save your resume');
          setTimeout(() => { navigate('/profile'); }, 1500);
        }
        return;
      }

      // Reuse existing resume ID if editing, otherwise generate new
      const currentDraftId = resumeId || `resume_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      
      // Render the current resume HTML
      const renderedHTML = renderTemplate(templateHTML, resumeData, { hiddenSections });
      
      // Upload HTML to Firebase Storage
      const htmlBlob = new Blob([renderedHTML], { type: 'text/html' });
      const htmlFileName = `${currentDraftId}.html`;
      const htmlRef = ref(storage, `users/${user.uid}/resumes/${htmlFileName}`);
      
      await uploadBytes(htmlRef, htmlBlob, {
        contentType: 'text/html',
        customMetadata: {
          resumeId: currentDraftId,
          uploadedBy: user.uid,
          uploadDate: new Date().toISOString()
        }
      });
      
      const htmlDownloadURL = await getDownloadURL(htmlRef);
      
      // Save resume to Firestore
      const resumeRef = doc(db, 'users', user.uid, 'resumes', currentDraftId);
      const resumeDocData = {
        data: resumeData,
        templateId: selectedTemplate?.id || null,
        templateName: selectedTemplate?.name || 'Unknown',
        templateTier: selectedTemplate?.plan || selectedTemplate?.tier || 'free',
        templateHTML: templateHTML || '',
        htmlFileName: htmlFileName,
        htmlDownloadURL: htmlDownloadURL,
        htmlStoragePath: `users/${user.uid}/resumes/${htmlFileName}`,
        templateColorTheme: colorTheme || '',
        selectedFont: fontFamily || 'Calibri',
        hiddenSections: hiddenSections || [],
        lastFileSize: htmlBlob.size || 0,
        userId: user.uid,
        resumeName: (() => {
          const name = resumeData.fullName?.trim() || '';
          const title = resumeData.title?.trim() || '';
          return title ? `${name} - ${title}` : name;
        })(),
        updatedAt: serverTimestamp(),
        createdAt: serverTimestamp()
      };
      
      await setDoc(resumeRef, resumeDocData, { merge: true });
      
      // Update local resumeId state so subsequent saves reuse same ID
      setResumeId(currentDraftId);

      if (silent) {
        customToast.info('💾 Draft auto-saved', { autoClose: 2000 });
      } else {
        customToast.success('✅ Resume draft saved successfully!');
        // Navigate to home page after a brief delay
        setTimeout(() => { navigate('/'); }, 1500);
      }
      
    } catch (error) {
      console.error('❌ Save draft error:', error);
      if (!silent) customToast.error('Failed to save draft. Please try again.');
    } finally {
      setIsSavingDraft(false);
      isSavingRef.current = false;
    }
  };

  // Keep resumeDataRef in sync with latest state (for auto-save timer)
  useEffect(() => {
    resumeDataRef.current = resumeData;
  }, [resumeData]);

  // Keep handleSaveDraftRef in sync (so the timer always calls the latest version)
  useEffect(() => {
    handleSaveDraftRef.current = handleSaveDraft;
  });

  // Auto-save idle timer — save after 1 minute of no user interaction
  // Uses refs to always read the LATEST state when the timer fires,
  // regardless of when the effect was last created.
  useEffect(() => {
    const resetTimer = () => {
      clearTimeout(idleTimerRef.current);
      idleTimerRef.current = setTimeout(() => {
        // Only auto-save if user is logged in, has filled name, and isn't already saving
        if (auth.currentUser && resumeDataRef.current?.fullName?.trim() && !isSavingRef.current) {
          handleSaveDraftRef.current?.({ silent: true });
        }
      }, 60000); // 1 minute
    };

    const events = ['mousedown', 'keydown', 'touchstart', 'scroll'];
    events.forEach(e => document.addEventListener(e, resetTimer, { passive: true }));
    resetTimer(); // Start the timer on mount

    return () => {
      clearTimeout(idleTimerRef.current);
      events.forEach(e => document.removeEventListener(e, resetTimer));
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Inline form handlers - Project
  const openProjectForm = (index = null) => {
    if (index !== null) {
      setCurrentProject(resumeData.projects[index]);
      setEditingIndex(index);
    } else {
      setCurrentProject({
        projectName: '',
        projectDescription: '',
        technologies: '',
        projectDate: '',
        projectLink: ''
      });
      setEditingIndex(null);
    }
    setShowProjectForm(true);
    scrollToInlineForm();
  };

  const closeProjectForm = () => {
    setShowProjectForm(false);
    setEditingIndex(null);
  };

  const saveProject = () => {
    if (editingIndex !== null) {
      const updated = [...resumeData.projects];
      updated[editingIndex] = currentProject;
      setResumeData({ ...resumeData, projects: updated });
    } else {
      setResumeData({
        ...resumeData,
        projects: [...resumeData.projects, currentProject]
      });
    }
    closeProjectForm();
  };

  const deleteProject = (index) => {
    const updated = resumeData.projects.filter((_, i) => i !== index);
    setResumeData({ ...resumeData, projects: updated });
    // Close form if editing the deleted item or if index is stale
    if (showProjectForm && (editingIndex === index || editingIndex >= updated.length)) {
      closeProjectForm();
    }
  };

  // Inline form handlers - Certification
  const openCertificationForm = (index = null) => {
    if (index !== null) {
      setCurrentCertification(resumeData.certifications[index]);
      setEditingIndex(index);
    } else {
      setCurrentCertification({
        certificationName: '',
        issuingOrganization: '',
        certificationDate: '',
        credentialId: ''
      });
      setEditingIndex(null);
    }
    setShowCertificationForm(true);
    scrollToInlineForm();
  };

  const closeCertificationForm = () => {
    setShowCertificationForm(false);
    setEditingIndex(null);
  };

  const saveCertification = () => {
    if (editingIndex !== null) {
      const updated = [...resumeData.certifications];
      updated[editingIndex] = currentCertification;
      setResumeData({ ...resumeData, certifications: updated });
    } else {
      setResumeData({
        ...resumeData,
        certifications: [...resumeData.certifications, currentCertification]
      });
    }
    closeCertificationForm();
  };

  const deleteCertification = (index) => {
    const updated = resumeData.certifications.filter((_, i) => i !== index);
    setResumeData({ ...resumeData, certifications: updated });
    // Close form if editing the deleted item or if index is stale
    if (showCertificationForm && (editingIndex === index || editingIndex >= updated.length)) {
      closeCertificationForm();
    }
  };

  // Inline form handlers - Language
  const openLanguageForm = (index = null) => {
    if (index !== null) {
      setCurrentLanguage(resumeData.languages[index]);
      setEditingIndex(index);
    } else {
      setCurrentLanguage({
        language: '',
        proficiency: 'Professional Working'
      });
      setEditingIndex(null);
    }
    setShowLanguageForm(true);
  };

  const closeLanguageForm = () => {
    setShowLanguageForm(false);
    setEditingIndex(null);
  };

  const saveLanguage = () => {
    if (!currentLanguage.language.trim()) return;
    if (editingIndex !== null) {
      const updated = [...resumeData.languages];
      updated[editingIndex] = currentLanguage;
      setResumeData({ ...resumeData, languages: updated });
    } else {
      setResumeData({
        ...resumeData,
        languages: [...resumeData.languages, currentLanguage]
      });
    }
    closeLanguageForm();
  };

  const deleteLanguage = (index) => {
    const updated = resumeData.languages.filter((_, i) => i !== index);
    setResumeData({ ...resumeData, languages: updated });
  };

  // ─── Mandatory field validation per section ───
  const validateCurrentStep = () => {
    const stepId = steps[currentStep]?.id;
    const missing = [];

    switch (stepId) {
      case 'personal':
        if (!resumeData.fullName?.trim()) missing.push('Full Name');
        if (!resumeData.email?.trim()) missing.push('Email');
        if (!resumeData.phone?.trim()) missing.push('Phone');
        if (!resumeData.location?.trim()) missing.push('Location');
        break;

      case 'summary':
        if (!resumeData.summary?.trim()) missing.push('Professional Summary (generate or write your own)');
        break;

      case 'experience':
        // Only validate if user has added experience entries
        if ((resumeData.experiences || []).length > 0) {
          (resumeData.experiences || []).forEach((exp, i) => {
            const prefix = resumeData.experiences.length > 1 ? `Experience ${i + 1}: ` : '';
            if (!exp.jobTitle?.trim()) missing.push(`${prefix}Job Title`);
            if (!exp.company?.trim()) missing.push(`${prefix}Company`);
            if (!exp.startDate?.trim()) missing.push(`${prefix}Start Date`);
            if (!exp.currentlyWorking && !exp.endDate?.trim()) missing.push(`${prefix}End Date`);
            if (!exp.responsibilities?.trim()) missing.push(`${prefix}Responsibilities`);
          });
        }
        break;

      case 'education':
        if ((resumeData.education || []).length === 0) {
          missing.push('At least one education entry');
        } else {
          (resumeData.education || []).forEach((edu, i) => {
            const prefix = resumeData.education.length > 1 ? `Education ${i + 1}: ` : '';
            if (!edu.degree?.trim()) missing.push(`${prefix}Degree`);
            if (!edu.institution?.trim()) missing.push(`${prefix}Institution`);
            if (!edu.graduationDate?.trim()) missing.push(`${prefix}Graduation Date`);
          });
        }
        break;

      case 'skills':
        // No mandatory fields for Skills section
        break;

      case 'projects':
        // Only validate if user has added projects
        if ((resumeData.projects || []).length > 0) {
          (resumeData.projects || []).forEach((proj, i) => {
            const prefix = resumeData.projects.length > 1 ? `Project ${i + 1}: ` : '';
            if (!proj.projectName?.trim()) missing.push(`${prefix}Project Name`);
            if (!proj.projectDescription?.trim()) missing.push(`${prefix}Description`);
          });
        }
        break;

      case 'certifications':
        // Only validate if user has added certifications
        if ((resumeData.certifications || []).length > 0) {
          (resumeData.certifications || []).forEach((cert, i) => {
            const prefix = resumeData.certifications.length > 1 ? `Certification ${i + 1}: ` : '';
            if (!cert.certificationName?.trim()) missing.push(`${prefix}Certification Name`);
            if (!cert.issuingOrganization?.trim()) missing.push(`${prefix}Issuing Organization`);
            if (!(cert.certificationDate || cert.issueDate)?.trim()) missing.push(`${prefix}Issuing Date`);
          });
        }
        break;

      default:
        break;
    }

    return missing;
  };

  // Handle step change (sidebar click)
  const handleStepChange = (index) => {
    // Allow navigating backward freely, only validate going forward
    if (index > currentStep) {
      const missing = validateCurrentStep();
      if (missing.length > 0) {
        customToast.warning(`Please fill mandatory fields:\n• ${missing.slice(0, 3).join('\n• ')}${missing.length > 3 ? `\n  ...and ${missing.length - 3} more` : ''}`);
        return;
      }
    }
    setCurrentStep(index);
  };

  // Handle form input change
  const handleInputChange = (field, value) => {
    setResumeData(prev => ({ ...prev, [field]: value }));
  };

  // Navigate to previous/next step
  const handleNext = () => {
    if (currentStep < steps.length - 1) {
      const missing = validateCurrentStep();
      if (missing.length > 0) {
        customToast.warning(`Please fill mandatory fields:\n• ${missing.slice(0, 3).join('\n• ')}${missing.length > 3 ? `\n  ...and ${missing.length - 3} more` : ''}`);
        return;
      }
      setCurrentStep(currentStep + 1);
    }
  };

  const handlePrevious = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  // Render step content
  const renderStepContent = () => {
    switch (steps[currentStep].id) {
      case 'personal':
        return renderPersonalInfoForm();
      case 'summary':
        return renderSummaryForm();
      case 'experience':
        return renderExperienceForm();
      case 'education':
        return renderEducationForm();
      case 'skills':
        return renderSkillsForm();
      case 'projects':
        return renderProjectsForm();
      case 'certifications':
        return renderCertificationsForm();
      case 'preview':
        return renderPreviewContent();
      default:
        return null;
    }
  };

  // Form renderers

  // Check if template supports photo upload (platinum templates or templates with photo tokens)
  const supportsPhoto = () => {
    try {
      const tier = (selectedTemplate?.tier || '').toLowerCase();
      const templateId = (selectedTemplate?.id || selectedTemplate?.name || '').toLowerCase();
      const templatePath = (selectedTemplate?.storagePath || selectedTemplate?.path || '').toLowerCase();
      
      // Platinum templates always support photo
      if (tier === 'platinum' || templateId.includes('platinum') || templatePath.includes('/platinum/')) {
        return true;
      }
      
      // Check if template HTML has photo tokens
      if (templateHTML) {
        const photoTokens = ['photoURL', 'photo', 'profilePhoto', 'avatar', 'headshot', 'image', 'picture'];
        const hasPhotoToken = photoTokens.some(token => 
          templateHTML.includes(`{{${token}}}`) || templateHTML.includes(`{{${token}`)
        );
        if (hasPhotoToken) return true;
        
        // Check for img tags with Mustache tokens
        if (/<img[^>]*\{\{[^}]+\}\}[^>]*>/i.test(templateHTML)) return true;
      }
      
      return false;
    } catch (err) {
      console.error('Photo support check error:', err);
      return false;
    }
  };

  // Check if template supports soft skills section
  const supportsSoftSkills = () => {
    if (!templateHTML) return true; // Show by default if template not loaded yet
    const softSkillTokens = ['softSkills', 'softSkillsJoined', 'softSkillsJoinedBullet', 'hasSoftSkills'];
    return softSkillTokens.some(token => 
      templateHTML.includes(`{{${token}}}`) || templateHTML.includes(`{{#${token}}}`) || templateHTML.includes(`{{${token}`)
    );
  };

  // Check if template supports languages section
  const supportsLanguages = () => {
    if (!templateHTML) return true; // Show by default if template not loaded yet
    const langTokens = ['languages', 'hasLanguages', 'languagesFormatted', 'languagesJoined'];
    return langTokens.some(token => 
      templateHTML.includes(`{{${token}}}`) || templateHTML.includes(`{{#${token}}}`) || templateHTML.includes(`{{${token}`)
    );
  };

  const handlePhotoUpload = (croppedImage) => {
    setPhotoPreview(croppedImage.dataUrl);
    setResumeData(prev => ({
      ...prev,
      photoURL: croppedImage.dataUrl,
      photoDataUri: croppedImage.dataUrl
    }));
  };

  const renderPersonalInfoForm = () => (
    <div className="rev2-form-content">
      <div className="rev2-form-section">
        <h3 className="rev2-section-title">
          <span className="rev2-title-icon">👤</span>
          Personal Information
        </h3>
        
        <div className="rev2-form-grid">
          <div className="rev2-form-group">
            <label className="rev2-label">Full Name *</label>
            <input
              type="text"
              className="rev2-input"
              placeholder="John Doe"
              value={resumeData.fullName}
              onChange={(e) => handleInputChange('fullName', e.target.value)}
            />
          </div>

          <div className="rev2-form-group">
            <label className="rev2-label">Professional Title</label>
            <input
              type="text"
              className="rev2-input"
              placeholder="Software Engineer"
              value={resumeData.title}
              onChange={(e) => handleInputChange('title', e.target.value)}
            />
          </div>

          <div className="rev2-form-group">
            <label className="rev2-label">Email *</label>
            <input
              type="email"
              className="rev2-input"
              placeholder="john.doe@email.com"
              value={resumeData.email}
              onChange={(e) => handleInputChange('email', e.target.value)}
            />
          </div>

          <div className="rev2-form-group">
            <label className="rev2-label">Phone *</label>
            <input
              type="tel"
              className="rev2-input"
              placeholder="+1 (555) 123-4567"
              value={resumeData.phone}
              onChange={(e) => handleInputChange('phone', e.target.value)}
            />
          </div>

          <div className="rev2-form-group">
            <label className="rev2-label">Location *</label>
            <input
              type="text"
              className="rev2-input"
              placeholder="New York, NY"
              value={resumeData.location}
              onChange={(e) => handleInputChange('location', e.target.value)}
            />
          </div>

          <div className="rev2-form-group">
            <label className="rev2-label">LinkedIn</label>
            <input
              type="url"
              className="rev2-input"
              placeholder="linkedin.com/in/johndoe"
              value={resumeData.linkedin}
              onChange={(e) => handleInputChange('linkedin', e.target.value)}
            />
          </div>

          <div className="rev2-form-group">
            <label className="rev2-label">GitHub</label>
            <input
              type="url"
              className="rev2-input"
              placeholder="github.com/johndoe"
              value={resumeData.github}
              onChange={(e) => handleInputChange('github', e.target.value)}
            />
          </div>

          <div className="rev2-form-group">
            <label className="rev2-label">Portfolio/Website</label>
            <input
              type="url"
              className="rev2-input"
              placeholder="www.johndoe.com"
              value={resumeData.portfolio}
              onChange={(e) => handleInputChange('portfolio', e.target.value)}
            />
          </div>
          <p className="rev2-note" style={{ marginTop: '10px', fontSize: '12px', color: '#1d4ed8', background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: '8px', padding: '10px 12px', lineHeight: '1.5' }}>
            💡 <strong>Tip:</strong> Portfolio/LinkedIn/GitHub URLs will be displayed as clickable hyperlinks in your resume.
          </p>
        </div>

        {/* Photo Upload — visible only for platinum templates or templates with photo tokens */}
        {supportsPhoto() && (
          <div className="rev2-photo-upload-section" style={{
            marginTop: '20px',
            padding: '16px',
            background: 'linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%)',
            borderRadius: '12px',
            border: '1px solid #bae6fd'
          }}>
            <label className="rev2-label" style={{ marginBottom: '10px', display: 'block', fontWeight: 600 }}>
              📷 Profile Photo <span style={{ fontSize: '11px', color: '#64748b', fontWeight: 400 }}>(Platinum feature)</span>
            </label>
            <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
              {(photoPreview || resumeData.photoURL) && (
                <div style={{
                  width: '72px',
                  height: '72px',
                  borderRadius: '50%',
                  overflow: 'hidden',
                  border: '3px solid #3b82f6',
                  boxShadow: '0 2px 8px rgba(59,130,246,0.3)',
                  flexShrink: 0
                }}>
                  <img 
                    src={photoPreview || resumeData.photoURL} 
                    alt="Profile" 
                    style={{ width: '100%', height: '100%', objectFit: 'cover' }} 
                  />
                </div>
              )}
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                <button
                  type="button"
                  onClick={() => setShowPhotoModal(true)}
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '8px',
                    padding: '8px 16px',
                    background: '#3b82f6',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    fontSize: '13px',
                    fontWeight: 600,
                    transition: 'background 0.2s'
                  }}
                  onMouseOver={(e) => e.target.style.background = '#2563eb'}
                  onMouseOut={(e) => e.target.style.background = '#3b82f6'}
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z"></path>
                    <circle cx="12" cy="13" r="4"></circle>
                  </svg>
                  {(photoPreview || resumeData.photoURL) ? 'Change Photo' : 'Upload Photo'}
                </button>
                {(photoPreview || resumeData.photoURL) && (
                  <button
                    type="button"
                    onClick={() => {
                      setPhotoPreview('');
                      setResumeData(prev => ({ ...prev, photoURL: '', photoDataUri: '' }));
                    }}
                    style={{
                      padding: '6px 12px',
                      background: 'transparent',
                      color: '#ef4444',
                      border: '1px solid #fca5a5',
                      borderRadius: '6px',
                      cursor: 'pointer',
                      fontSize: '12px',
                      fontWeight: 500
                    }}
                  >
                    🗑️ Remove
                  </button>
                )}
                <small style={{ color: '#64748b', fontSize: '11px' }}>Square format recommended, max 2MB</small>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );

  const renderSummaryForm = () => (
    <div className="rev2-form-content">
      <div className="rev2-form-section">
        <ProfessionalSummaryGenerator
          value={resumeData.summary}
          onChange={(val) => handleInputChange('summary', val)}
        />
      </div>
    </div>
  );

  const renderExperienceForm = () => (
    <div className="rev2-form-content">
      <div className="rev2-form-section">
        <h3 className="rev2-section-title">
          <span className="rev2-title-icon">💼</span>
          Work Experience
        </h3>
        <p className="rev2-section-desc">Add your professional work experience</p>
        
        <button className="rev2-add-btn" onClick={() => openExperienceForm()}>
          <span>+</span> Add Experience
        </button>

        {(resumeData.experiences || []).length > 0 && (
          <div className="rev2-items-list">
            {resumeData.experiences.map((exp, index) => (
              <div key={index} className="rev2-item-card">
                <div className="rev2-item-header">
                  <h4 className="rev2-item-title">{exp.jobTitle}</h4>
                  <div className="rev2-item-actions">
                    <button 
                      className="rev2-item-edit-btn"
                      onClick={() => openExperienceForm(index)}
                      title="Edit"
                    >
                      ✏️
                    </button>
                    <button 
                      className="rev2-item-delete-btn"
                      onClick={() => deleteExperience(index)}
                      title="Delete"
                    >
                      🗑️
                    </button>
                  </div>
                </div>
                <p className="rev2-item-subtitle">{exp.company}</p>
                <p className="rev2-item-detail">{exp.startDate} - {exp.currentlyWorking ? 'Present' : exp.endDate}</p>
              </div>
            ))}
          </div>
        )}

        {/* Inline Add/Edit Form */}
        {showExperienceForm && (
          <div className="rev2-inline-add-form">
            <div className="rev2-inline-form-header">
              <h4 className="rev2-inline-form-title">
                <span>💼</span>
                {editingIndex !== null ? 'Edit Experience' : 'Add Experience'}
              </h4>
              <button className="rev2-inline-form-close" onClick={closeExperienceForm}>✕</button>
            </div>

            <div className="rev2-form-group">
              <label className="rev2-label">Job Title *</label>
              <input
                type="text"
                className="rev2-input"
                value={currentExperience.jobTitle}
                onChange={(e) => setCurrentExperience({ ...currentExperience, jobTitle: e.target.value })}
                placeholder="e.g., Software Engineer"
              />
            </div>

            <div className="rev2-form-group">
              <label className="rev2-label">Company *</label>
              <input
                type="text"
                className="rev2-input"
                value={currentExperience.company}
                onChange={(e) => setCurrentExperience({ ...currentExperience, company: e.target.value })}
                placeholder="e.g., Tech Corp"
              />
            </div>

            <div className="rev2-form-group">
              <label className="rev2-label">Location</label>
              <input
                type="text"
                className="rev2-input"
                value={currentExperience.experienceLocation}
                onChange={(e) => setCurrentExperience({ ...currentExperience, experienceLocation: e.target.value })}
                placeholder="e.g., San Francisco, CA"
              />
            </div>

            <div className="rev2-form-row">
              <div className="rev2-form-group">
                <label className="rev2-label">Start Date *</label>
                <input
                  type="text"
                  className="rev2-input"
                  value={currentExperience.startDate}
                  onChange={(e) => setCurrentExperience({ ...currentExperience, startDate: e.target.value })}
                  placeholder="e.g., Jan 2022 or 2022-01"
                />
              </div>

              <div className="rev2-form-group">
                <label className="rev2-label">End Date {!currentExperience.currentlyWorking && '*'}</label>
                <input
                  type="text"
                  className={`rev2-input ${currentExperience.currentlyWorking ? 'rev2-input-disabled' : ''}`}
                  value={currentExperience.currentlyWorking ? 'Present' : currentExperience.endDate}
                  onChange={(e) => setCurrentExperience({ ...currentExperience, endDate: e.target.value })}
                  disabled={currentExperience.currentlyWorking}
                  placeholder="e.g., Dec 2024 or Present"
                  style={currentExperience.currentlyWorking ? { opacity: 0.6, backgroundColor: '#f3f4f6', fontStyle: 'italic' } : {}}
                />
              </div>
            </div>

            <div className="rev2-form-group">
              <label className="rev2-checkbox-label">
                <input
                  type="checkbox"
                  className="rev2-checkbox"
                  checked={currentExperience.currentlyWorking}
                  onChange={(e) => {
                    const isChecking = e.target.checked;
                    if (isChecking) {
                      // Check if another experience already has currentlyWorking = true
                      const existingPresentIndex = resumeData.experiences.findIndex(
                        (exp, idx) => exp.currentlyWorking && idx !== editingIndex
                      );
                      if (existingPresentIndex !== -1) {
                        customToast.warning(`You already have a present/current job: "${resumeData.experiences[existingPresentIndex].jobTitle || 'Untitled'}". Only one current position is allowed.`);
                        return;
                      }
                    }
                    setCurrentExperience({ 
                      ...currentExperience, 
                      currentlyWorking: isChecking,
                      endDate: isChecking ? '' : currentExperience.endDate
                    });
                  }}
                />
                <span>I currently work here</span>
              </label>
            </div>

            <div className="rev2-form-group">
              <label className="rev2-label">Responsibilities</label>
              <textarea
                className="rev2-textarea"
                rows="5"
                value={currentExperience.responsibilities}
                onChange={(e) => setCurrentExperience({ ...currentExperience, responsibilities: e.target.value })}
                placeholder={"• Show contributions and learning outcomes\n• Highlight project ownership and improvements\n• Emphasize strategic impact and leadership\n• Show enterprise-level results and decision making"}
              />
            </div>

            <div className="rev2-inline-form-actions">
              <button className="rev2-form-cancel-btn" onClick={closeExperienceForm}>
                Cancel
              </button>
              <button className="rev2-form-save-btn" onClick={saveExperience}>
                {editingIndex !== null ? 'Update' : 'Add'} Experience
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );

  const renderEducationForm = () => (
    <div className="rev2-form-content">
      <div className="rev2-form-section">
        <h3 className="rev2-section-title">
          <span className="rev2-title-icon">🎓</span>
          Education
        </h3>
        <p className="rev2-section-desc">Add your educational background</p>
        
        <button className="rev2-add-btn" onClick={() => openEducationForm()}>
          <span>+</span> Add Education
        </button>

        {(resumeData.education || []).length > 0 && (
          <div className="rev2-items-list">
            {(resumeData.education || []).map((edu, index) => (
              <div key={index} className="rev2-item-card">
                <div className="rev2-item-header">
                  <h4 className="rev2-item-title">{edu.degree}</h4>
                  <div className="rev2-item-actions">
                    <button 
                      className="rev2-item-edit-btn"
                      onClick={() => openEducationForm(index)}
                      title="Edit"
                    >
                      ✏️
                    </button>
                    <button 
                      className="rev2-item-delete-btn"
                      onClick={() => deleteEducation(index)}
                      title="Delete"
                    >
                      🗑️
                    </button>
                  </div>
                </div>
                <p className="rev2-item-subtitle">{edu.institution}</p>
                <p className="rev2-item-detail">{edu.graduationDate}</p>
              </div>
            ))}
          </div>
        )}

        {/* Inline Add/Edit Form */}
        {showEducationForm && (
          <div className="rev2-inline-add-form">
            <div className="rev2-inline-form-header">
              <h4 className="rev2-inline-form-title">
                <span>🎓</span>
                {editingIndex !== null ? 'Edit Education' : 'Add Education'}
              </h4>
              <button className="rev2-inline-form-close" onClick={closeEducationForm}>✕</button>
            </div>

            <div className="rev2-form-group">
              <label className="rev2-label">Degree *</label>
              <input
                type="text"
                className="rev2-input"
                value={currentEducation.degree}
                onChange={(e) => setCurrentEducation({ ...currentEducation, degree: e.target.value })}
                placeholder="e.g., Bachelor of Science in Computer Science"
              />
            </div>

            <div className="rev2-form-group">
              <label className="rev2-label">Institution *</label>
              <input
                type="text"
                className="rev2-input"
                value={currentEducation.institution}
                onChange={(e) => setCurrentEducation({ ...currentEducation, institution: e.target.value })}
                placeholder="e.g., University of California"
              />
            </div>

            <div className="rev2-form-row">
              <div className="rev2-form-group">
                <label className="rev2-label">Graduation Date *</label>
                <input
                  type="text"
                  className="rev2-input"
                  value={currentEducation.graduationDate}
                  onChange={(e) => setCurrentEducation({ ...currentEducation, graduationDate: e.target.value })}
                  placeholder="e.g., May 2023 or 2023-05"
                />
              </div>

              <div className="rev2-form-group">
                <label className="rev2-label">GPA</label>
                <input
                  type="text"
                  className="rev2-input"
                  value={currentEducation.gpa}
                  onChange={(e) => setCurrentEducation({ ...currentEducation, gpa: e.target.value })}
                  placeholder="e.g., 9.2/10 or 3.8/4.0 or 85%"
                />
              </div>
            </div>

            <div className="rev2-form-group">
              <label className="rev2-label">Location</label>
              <input
                type="text"
                className="rev2-input"
                value={currentEducation.educationLocation}
                onChange={(e) => setCurrentEducation({ ...currentEducation, educationLocation: e.target.value })}
                placeholder="e.g., Berkeley, CA"
              />
            </div>

            <div className="rev2-inline-form-actions">
              <button className="rev2-form-cancel-btn" onClick={closeEducationForm}>
                Cancel
              </button>
              <button className="rev2-form-save-btn" onClick={saveEducation}>
                {editingIndex !== null ? 'Update' : 'Add'} Education
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );

  const renderSkillsForm = () => (
    <div className="rev2-form-content">
      <div className="rev2-form-section">
        <h3 className="rev2-section-title">
          <span className="rev2-title-icon">⚡</span>
          Skills
        </h3>
        <p className="rev2-section-desc">Add your technical and soft skills</p>
        
        {/* Technical Skills */}
        <div className="rev2-form-group">
          <label className="rev2-label">Technical Skills</label>
          <div className="rev2-skills-autocomplete" ref={skillsInputRef}>
            <input
              type="text"
              className="rev2-input"
              placeholder={editingSkillIndex !== null ? "Edit skill and press Enter..." : "Type to search skills (e.g., JavaScript, React, Python)..."}
              value={skillInput}
              onChange={handleSkillInputChange}
              onKeyDown={(e) => {
                if (editingSkillIndex !== null && e.key === 'Enter') {
                  e.preventDefault();
                  confirmEditTechnicalSkill();
                  return;
                }
                if (editingSkillIndex !== null && e.key === 'Escape') {
                  e.preventDefault();
                  setEditingSkillIndex(null);
                  setSkillInput('');
                  return;
                }
                handleSkillKeyPress(e);
              }}
              onFocus={() => skillInput && setShowSkillsDropdown(true)}
            />
            {editingSkillIndex !== null && (
              <div className="rev2-edit-skill-hint">
                <span>Editing: Press Enter to save, Escape to cancel</span>
                <button onClick={() => { setEditingSkillIndex(null); setSkillInput(''); }}>Cancel</button>
              </div>
            )}
            
            {showSkillsDropdown && filteredSkills.length > 0 && (
              <div className="rev2-skills-dropdown" ref={skillsDropdownRef}>
                <div className="rev2-skills-dropdown-header">
                  <span className="rev2-skills-dropdown-count">{filteredSkills.length} found</span>
                </div>
                <div className="rev2-skills-dropdown-list">
                  {filteredSkills.map((skill, index) => (
                    <div
                      key={index}
                      className={`rev2-skills-dropdown-item ${highlightedSkillIndex === index ? 'rev2-skills-dropdown-item-highlighted' : ''}`}
                      onClick={() => addTechnicalSkill(skill)}
                      onMouseEnter={() => setHighlightedSkillIndex(index)}
                    >
                      <span className="rev2-skills-dropdown-name">{skill}</span>
                      {(resumeData.technicalSkills || []).includes(skill) && (
                        <span className="rev2-skills-dropdown-badge">Added</span>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Technical Skills Tags */}
          {(resumeData.technicalSkills || []).length > 0 && (
            <div className="rev2-skills-tags">
              {(resumeData.technicalSkills || []).map((skill, index) => (
                <span key={index} className="rev2-skill-tag">
                  <span className="rev2-skill-tag-text">{skill}</span>
                  <button
                    className="rev2-skill-tag-edit"
                    onClick={() => startEditTechnicalSkill(index)}
                    title="Edit skill"
                  >
                    ✏️
                  </button>
                  <button
                    className="rev2-skill-tag-remove"
                    onClick={() => removeTechnicalSkill(skill)}
                    title="Remove skill"
                  >
                    ×
                  </button>
                </span>
              ))}
            </div>
          )}
        </div>

        {/* Soft Skills */}
        {supportsSoftSkills() ? (
        <div className="rev2-form-group">
          <label className="rev2-label">Soft Skills</label>
          <div className="rev2-skills-autocomplete">
            <input
              type="text"
              className="rev2-input"
              placeholder={editingSoftSkillIndex !== null ? "Edit skill and press Enter..." : "Type to search skills (e.g., Leadership, Communication)..."}
              value={softSkillInput}
              onChange={handleSoftSkillInputChange}
              onKeyDown={(e) => {
                if (editingSoftSkillIndex !== null && e.key === 'Enter') {
                  e.preventDefault();
                  confirmEditSoftSkill();
                  return;
                }
                if (editingSoftSkillIndex !== null && e.key === 'Escape') {
                  e.preventDefault();
                  setEditingSoftSkillIndex(null);
                  setSoftSkillInput('');
                  return;
                }
                handleSoftSkillKeyPress(e);
              }}
              onFocus={() => softSkillInput && setShowSoftSkillsDropdown(true)}
            />
            {editingSoftSkillIndex !== null && (
              <div className="rev2-edit-skill-hint">
                <span>Editing: Press Enter to save, Escape to cancel</span>
                <button onClick={() => { setEditingSoftSkillIndex(null); setSoftSkillInput(''); }}>Cancel</button>
              </div>
            )}
            
            {showSoftSkillsDropdown && filteredSoftSkills.length > 0 && (
              <div className="rev2-skills-dropdown" ref={skillsDropdownRef}>
                <div className="rev2-skills-dropdown-header">
                  <span className="rev2-skills-dropdown-count">{filteredSoftSkills.length} found</span>
                </div>
                <div className="rev2-skills-dropdown-list">
                  {filteredSoftSkills.map((skill, index) => (
                    <div
                      key={index}
                      className={`rev2-skills-dropdown-item ${highlightedSoftSkillIndex === index ? 'rev2-skills-dropdown-item-highlighted' : ''}`}
                      onClick={() => addSoftSkill(skill)}
                      onMouseEnter={() => setHighlightedSoftSkillIndex(index)}
                    >
                      <span className="rev2-skills-dropdown-name">{skill}</span>
                      {(resumeData.softSkills || []).includes(skill) && (
                        <span className="rev2-skills-dropdown-badge">Added</span>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Soft Skills Tags */}
          {(resumeData.softSkills || []).length > 0 && (
            <div className="rev2-skills-tags">
              {(resumeData.softSkills || []).map((skill, index) => (
                <span key={index} className="rev2-skill-tag rev2-skill-tag-soft">
                  <span className="rev2-skill-tag-text">{skill}</span>
                  <button
                    className="rev2-skill-tag-edit"
                    onClick={() => startEditSoftSkill(index)}
                    title="Edit skill"
                  >
                    ✏️
                  </button>
                  <button
                    className="rev2-skill-tag-remove"
                    onClick={() => removeSoftSkill(skill)}
                    title="Remove skill"
                  >
                    ×
                  </button>
                </span>
              ))}
            </div>
          )}
        </div>
        ) : (
          <div className="rev2-form-group" style={{ opacity: 0.6, padding: '12px', background: '#f8fafc', borderRadius: '8px' }}>
            <p className="rev2-hint" style={{ margin: 0 }}>ℹ️ This template does not include a Soft Skills section.</p>
          </div>
        )}

        <p className="rev2-hint">
          💡 Tip: Type to search from 150+ predefined skills or press Enter to add custom skills
        </p>

        {/* Languages */}
        {supportsLanguages() ? (
        <div className="rev2-form-group" style={{ marginTop: '24px', paddingTop: '20px', borderTop: '1px solid #e2e8f0' }}>
          <label className="rev2-label" style={{ fontSize: '15px', fontWeight: 600 }}>🌐 Languages</label>
          <p className="rev2-section-desc" style={{ marginBottom: '12px' }}>Add languages you speak (used in 12+ templates)</p>
          
          <button className="rev2-add-btn" onClick={() => openLanguageForm()} style={{ marginBottom: '12px' }}>
            <span>+</span> Add Language
          </button>

          {(resumeData.languages || []).length > 0 && (
            <div className="rev2-skills-tags" style={{ marginBottom: '12px' }}>
              {(resumeData.languages || []).map((lang, index) => (
                <span key={index} className="rev2-skill-tag" style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
                  <span className="rev2-skill-tag-text">
                    {typeof lang === 'string' ? lang : `${lang.language}${lang.proficiency ? ` (${lang.proficiency})` : ''}`}
                  </span>
                  <button
                    onClick={() => openLanguageForm(index)}
                    title="Edit language"
                    className="rev2-skill-tag-edit"
                  >
                    ✏️
                  </button>
                  <button
                    className="rev2-skill-tag-remove"
                    onClick={() => deleteLanguage(index)}
                    title="Remove language"
                  >
                    ×
                  </button>
                </span>
              ))}
            </div>
          )}

          {showLanguageForm && (
            <div className="rev2-inline-add-form" style={{ marginTop: '8px' }}>
              <div className="rev2-inline-form-header">
                <h4 className="rev2-inline-form-title">
                  <span>🌐</span>
                  {editingIndex !== null ? 'Edit Language' : 'Add Language'}
                </h4>
                <button className="rev2-inline-form-close" onClick={closeLanguageForm}>✕</button>
              </div>

              <div className="rev2-form-row" style={{ display: 'flex', gap: '12px' }}>
                <div className="rev2-form-group" style={{ flex: 1 }}>
                  <label className="rev2-label">Language *</label>
                  <input
                    type="text"
                    className="rev2-input"
                    value={currentLanguage.language}
                    onChange={(e) => setCurrentLanguage({ ...currentLanguage, language: e.target.value })}
                    placeholder="e.g., English, Spanish, Mandarin"
                  />
                </div>

                <div className="rev2-form-group" style={{ flex: 1 }}>
                  <label className="rev2-label">Proficiency</label>
                  <select
                    className="rev2-input"
                    value={currentLanguage.proficiency}
                    onChange={(e) => setCurrentLanguage({ ...currentLanguage, proficiency: e.target.value })}
                  >
                    <option value="Native">Native</option>
                    <option value="Fluent">Fluent</option>
                    <option value="Professional Working">Professional Working</option>
                    <option value="Intermediate">Intermediate</option>
                    <option value="Elementary">Elementary</option>
                    <option value="Beginner">Beginner</option>
                  </select>
                </div>
              </div>

              <div className="rev2-inline-form-actions">
                <button className="rev2-form-cancel-btn" onClick={closeLanguageForm}>
                  Cancel
                </button>
                <button className="rev2-form-save-btn" onClick={saveLanguage}>
                  {editingIndex !== null ? 'Update' : 'Add'} Language
                </button>
              </div>
            </div>
          )}
        </div>
        ) : (
          <div className="rev2-form-group" style={{ marginTop: '24px', paddingTop: '20px', borderTop: '1px solid #e2e8f0', opacity: 0.6, padding: '12px', background: '#f8fafc', borderRadius: '8px' }}>
            <p className="rev2-hint" style={{ margin: 0 }}>ℹ️ This template does not include a Languages section.</p>
          </div>
        )}
      </div>
    </div>
  );

  const renderProjectsForm = () => (
    <div className="rev2-form-content">
      <div className="rev2-form-section">
        <h3 className="rev2-section-title">
          <span className="rev2-title-icon">🚀</span>
          Projects
        </h3>
        <p className="rev2-section-desc">Showcase your notable projects</p>
        
        <button className="rev2-add-btn" onClick={() => openProjectForm()}>
          <span>+</span> Add Project
        </button>

        {(resumeData.projects || []).length > 0 && (
          <div className="rev2-items-list">
            {(resumeData.projects || []).map((proj, index) => (
              <div key={index} className="rev2-item-card">
                <div className="rev2-item-header">
                  <h4 className="rev2-item-title">{proj.projectName}</h4>
                  <div className="rev2-item-actions">
                    <button 
                      className="rev2-item-edit-btn"
                      onClick={() => openProjectForm(index)}
                      title="Edit"
                    >
                      ✏️
                    </button>
                    <button 
                      className="rev2-item-delete-btn"
                      onClick={() => deleteProject(index)}
                      title="Delete"
                    >
                      🗑️
                    </button>
                  </div>
                </div>
                <p className="rev2-item-detail">{proj.technologies}</p>
              </div>
            ))}
          </div>
        )}

        {/* Inline Add/Edit Form */}
        {showProjectForm && (
          <div className="rev2-inline-add-form">
            <div className="rev2-inline-form-header">
              <h4 className="rev2-inline-form-title">
                <span>🚀</span>
                {editingIndex !== null ? 'Edit Project' : 'Add Project'}
              </h4>
              <button className="rev2-inline-form-close" onClick={closeProjectForm}>✕</button>
            </div>

            <div className="rev2-form-group">
              <label className="rev2-label">Project Name *</label>
              <input
                type="text"
                className="rev2-input"
                value={currentProject.projectName}
                onChange={(e) => setCurrentProject({ ...currentProject, projectName: e.target.value })}
                placeholder="e.g., E-commerce Platform"
              />
            </div>

            <div className="rev2-form-group">
              <label className="rev2-label">Description *</label>
              <textarea
                className="rev2-textarea"
                rows="4"
                value={currentProject.projectDescription}
                onChange={(e) => setCurrentProject({ ...currentProject, projectDescription: e.target.value })}
                placeholder="Describe the project and your role..."
              />
            </div>

            <div className="rev2-form-group">
              <label className="rev2-label">Technologies Used <span style={{ fontSize: '0.75rem', color: '#64748b', fontWeight: 'normal' }}>(Comma Separated)</span></label>
              <input
                type="text"
                className="rev2-input"
                value={currentProject.technologies}
                onChange={(e) => setCurrentProject({ ...currentProject, technologies: e.target.value })}
                placeholder="e.g., React, Node.js, MongoDB"
              />
            </div>

            <div className="rev2-form-group">
              <label className="rev2-label">Project Date</label>
              <input
                type="text"
                className="rev2-input"
                value={currentProject.projectDate}
                onChange={(e) => setCurrentProject({ ...currentProject, projectDate: e.target.value })}
                placeholder="e.g., Jan 2024 - Mar 2024 or 2024"
              />
            </div>

            <div className="rev2-inline-form-actions">
              <button className="rev2-form-cancel-btn" onClick={closeProjectForm}>
                Cancel
              </button>
              <button className="rev2-form-save-btn" onClick={saveProject}>
                {editingIndex !== null ? 'Update' : 'Add'} Project
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );

  const renderCertificationsForm = () => (
    <div className="rev2-form-content">
      <div className="rev2-form-section">
        <h3 className="rev2-section-title">
          <span className="rev2-title-icon">🏆</span>
          Certifications
        </h3>
        <p className="rev2-section-desc">Add your professional certifications</p>
        
        <button className="rev2-add-btn" onClick={() => openCertificationForm()}>
          <span>+</span> Add Certification
        </button>

        {(resumeData.certifications || []).length > 0 && (
          <div className="rev2-items-list">
            {(resumeData.certifications || []).map((cert, index) => (
              <div key={index} className="rev2-item-card">
                <div className="rev2-item-header">
                  <h4 className="rev2-item-title">{cert.certificationName}</h4>
                  <div className="rev2-item-actions">
                    <button 
                      className="rev2-item-edit-btn"
                      onClick={() => openCertificationForm(index)}
                      title="Edit"
                    >
                      ✏️
                    </button>
                    <button 
                      className="rev2-item-delete-btn"
                      onClick={() => deleteCertification(index)}
                      title="Delete"
                    >
                      🗑️
                    </button>
                  </div>
                </div>
                <p className="rev2-item-subtitle">{cert.issuingOrganization}</p>
                <p className="rev2-item-detail">{cert.certificationDate || cert.issueDate}</p>
              </div>
            ))}
          </div>
        )}

        {/* Inline Add/Edit Form */}
        {showCertificationForm && (
          <div className="rev2-inline-add-form">
            <div className="rev2-inline-form-header">
              <h4 className="rev2-inline-form-title">
                <span>🏆</span>
                {editingIndex !== null ? 'Edit Certification' : 'Add Certification'}
              </h4>
              <button className="rev2-inline-form-close" onClick={closeCertificationForm}>✕</button>
            </div>

            <div className="rev2-form-group">
              <label className="rev2-label">Certification Name *</label>
              <input
                type="text"
                className="rev2-input"
                value={currentCertification.certificationName}
                onChange={(e) => setCurrentCertification({ ...currentCertification, certificationName: e.target.value })}
                placeholder="e.g., AWS Certified Solutions Architect"
              />
            </div>

            <div className="rev2-form-group">
              <label className="rev2-label">Issuing Organization *</label>
              <input
                type="text"
                className="rev2-input"
                value={currentCertification.issuingOrganization}
                onChange={(e) => setCurrentCertification({ ...currentCertification, issuingOrganization: e.target.value })}
                placeholder="e.g., Amazon Web Services"
              />
            </div>

            <div className="rev2-form-group">
              <label className="rev2-label">Issue Date *</label>
              <input
                type="text"
                className="rev2-input"
                value={currentCertification.certificationDate || currentCertification.issueDate || ''}
                onChange={(e) => setCurrentCertification({ ...currentCertification, certificationDate: e.target.value })}
                placeholder="e.g., Mar 2024 or 2024-03"
              />
            </div>

            <div className="rev2-inline-form-actions">
              <button className="rev2-form-cancel-btn" onClick={closeCertificationForm}>
                Cancel
              </button>
              <button className="rev2-form-save-btn" onClick={saveCertification}>
                {editingIndex !== null ? 'Update' : 'Add'} Certification
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );

  // Server Export Handler (from ResumeEditor v1)
  const handleServerExport = async (format) => {
    // Helper: save state to sessionStorage and navigate to pricing INSTANTLY,
    // then save draft to Firestore in background (non-blocking)
    const saveDraftThenNavigate = async (pricingState = {}) => {
      const user = auth.currentUser;
      const currentDraftId = resumeId || `resume_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

      // ── Step 1: Save to sessionStorage IMMEDIATELY (instant, no network) ──
      // NOTE: photoPreview (base64) and previewHTML are excluded to avoid
      // exceeding sessionStorage 5MB quota. Photo URL is in resumeData.photoURL.
      saveResumeEditorState({
        resumeData,
        templateHTML,
        selectedTemplate,
        colorTheme,
        fontFamily,
        hiddenSections,
        currentStep,
        photoPreview: '', // excluded — base64 too large
        resumeId: currentDraftId,
        previewHTML: '', // excluded — re-rendered on restore
      });

      // ── Step 2: Navigate to pricing IMMEDIATELY (no waiting for Firestore) ──
      navigate('/pricing', {
        state: {
          ...pricingState,
          returnUrl: '/resume-editor-v2',
          returnAction: pricingState.returnAction || 'download',
          draftSavedToMyFiles: true,
          resumeId: currentDraftId
        }
      });

      // ── Step 3: Save to Firestore/Storage in background (fire-and-forget) ──
      // This runs after navigation — if it fails, sessionStorage still has the data
      if (user && resumeData?.fullName?.trim()) {
        try {
          const renderedHTML = renderTemplate(templateHTML, resumeData, { hiddenSections });
          const htmlBlob = new Blob([renderedHTML], { type: 'text/html' });
          const htmlFileName = `${currentDraftId}.html`;
          const htmlRef = ref(storage, `users/${user.uid}/resumes/${htmlFileName}`);
          uploadBytes(htmlRef, htmlBlob, {
            contentType: 'text/html',
            customMetadata: { resumeId: currentDraftId, uploadedBy: user.uid, uploadDate: new Date().toISOString() }
          }).then(() => getDownloadURL(htmlRef)).then(htmlDownloadURL => {
            const resumeRef = doc(db, 'users', user.uid, 'resumes', currentDraftId);
            setDoc(resumeRef, {
              data: resumeData,
              templateId: selectedTemplate?.id || null,
              templateName: selectedTemplate?.name || 'Unknown',
              templateTier: selectedTemplate?.plan || selectedTemplate?.tier || 'free',
              templateHTML: templateHTML || '',
              htmlFileName, htmlDownloadURL,
              htmlStoragePath: `users/${user.uid}/resumes/${htmlFileName}`,
              templateColorTheme: colorTheme || '',
              selectedFont: fontFamily || 'Calibri',
              hiddenSections: hiddenSections || [],
              lastFileSize: htmlBlob.size || 0,
              userId: user.uid,
              resumeName: (() => {
                const name = resumeData.fullName?.trim() || '';
                const title = resumeData.title?.trim() || '';
                return title ? `${name} - ${title}` : name;
              })(),
              updatedAt: serverTimestamp(),
              createdAt: serverTimestamp()
            }, { merge: true });
          }).catch(err => console.warn('⚠️ Background draft save failed (non-critical):', err.message));
        } catch (err) {
          console.warn('⚠️ Background draft save failed:', err.message);
        }
      }
    };

    try {
      const user = auth.currentUser;
      if (!user) {
        navigate('/profile');
        return;
      }

      const userRef = doc(db, 'users', user.uid);
      const userSnap = await getDoc(userRef);
      const userData = userSnap.exists() ? userSnap.data() : { planType: 'free' };

      const plan = (userData.planType || 'free').toLowerCase();
      if (plan === 'free') {
        await saveDraftThenNavigate({
          message: 'Upgrade to download resumes and access premium templates',
          returnAction: 'download'
        });
        return;
      }

      // ═══ Plan-Tier Guard ═══
      // Users can only download templates that match their plan tier or below:
      // Silver → silver only | Gold → silver + gold | Platinum → silver + gold + platinum
      const templateTierRaw = (selectedTemplate?.plan || selectedTemplate?.tier || 'silver').toLowerCase();
      const tierHierarchy = { silver: 1, gold: 2, platinum: 3 };
      const userTierLevel = tierHierarchy[plan] || 0;
      const templateTierLevel = tierHierarchy[templateTierRaw] || 1;

      if (templateTierLevel > userTierLevel) {
        const tierNames = { silver: 'Silver', gold: 'Gold', platinum: 'Platinum' };
        customToast.error(
          `This is a ${tierNames[templateTierRaw] || templateTierRaw} template. Your current plan (${tierNames[plan] || plan}) does not include ${tierNames[templateTierRaw] || templateTierRaw} templates. Please upgrade your plan to download this template.`,
          { autoClose: 8000 }
        );
        return;
      }

      // Check plan expiry
      if (userData.planExpiryDate) {
        const expiryDate = userData.planExpiryDate.toDate ? userData.planExpiryDate.toDate() : new Date(userData.planExpiryDate);
        const now = new Date();
        
        // Expiry day itself is still valid — only expire after end of expiry day
        const expiryEndOfDay = new Date(expiryDate.getFullYear(), expiryDate.getMonth(), expiryDate.getDate(), 23, 59, 59);
        
        if (now > expiryEndOfDay) {
          const expiredDays = Math.floor((now - expiryEndOfDay) / (1000 * 60 * 60 * 24));
          if (window.confirm(`⏰ Your ${plan.toUpperCase()} plan expired ${expiredDays} day(s) ago.\n\nClick OK to upgrade your plan.`)) {
            await saveDraftThenNavigate({
              message: 'Your plan has expired. Renew to continue downloading.',
              returnAction: 'download'
            });
          }
          return;
        }
      }

      // Check download limits
      const resumeLimit = userData.resumeDownloadLimit ?? 0;
      const resumeUsed = userData.resumeDownloadsUsed ?? 0;
      const isUnlimited = resumeLimit === -1;

      if (!isUnlimited && resumeUsed >= resumeLimit) {
        // Determine template tier
        const currentTier = (selectedTemplate?.plan || selectedTemplate?.tier || '').toLowerCase();
        const addonEligibleAll = import.meta.env.VITE_ADDON_ELIGIBLE_ALL_TEMPLATES === 'true';
        const isAddonEligible = addonEligibleAll || currentTier === 'silver';

        if (isAddonEligible) {
          // Silver template or all-eligible flag → show LimitExceededModal (Buy addon)
          setShowAddonModal(true);
        } else {
          // Gold/Platinum template → just toast, no addon option
          customToast.error(`Download limit reached (${resumeUsed}/${resumeLimit}). Please upgrade your plan to download this template.`);
        }
        return;
      }

      // Validate resume data
      if (!canExportResume(resumeData)) {
        alert('⚠️ Please fill at least ONE section with data before exporting.');
        return;
      }

      setShowExportDialog(true);
      setExportProgress(0);
      setExportStatus('Preparing resume...');

      // Smooth progress animation: 0 → 10 → 30 → 50
      await new Promise(r => setTimeout(r, 200));
      setExportProgress(10);
      setExportStatus('Rendering resume...');

      const filteredData = filterResumeData(resumeData);
      
      const exportData = {
        ...filteredData,
      };

      const cleanedHTML = sanitizeTemplateHTML(templateHTML);
      let rendered = renderTemplate(cleanedHTML, exportData, { hiddenSections });
      
      // Apply color theme to export (CSS variable injection — same as preview)
      const palette = colorPalettes[colorTheme];
      if (palette && tierColorPolicy.enabled) {
        rendered = injectThemeIntoHTML(rendered, palette.primary);
      }
      
      // Apply font family to export — injects Google Font import so Puppeteer
      // downloads the web font before rendering the PDF
      rendered = injectFontIntoHTML(rendered, fontFamily);

      // Inject export-specific CSS that matches the live preview layout exactly
      // Backend forces 'screen' media type so @media print rules won't fire.
      // These rules are applied directly to ensure WYSIWYG PDF output.
      const exportCSS = `
        <style id="export-match-preview">
          /* Page 1: zero margin for full-bleed headers/ribbons */
          @page :first {
            size: A4;
            margin: 0mm;
          }
          /* Page 2+: small top margin so content doesn't start at the very edge */
          @page {
            size: A4;
            margin: 8mm 0mm 6mm 0mm;
          }
          /* Let the template body keep its own padding/margin for layout */
          html {
            width: auto !important;
            height: auto !important;
            overflow: visible !important;
          }
          body {
            height: auto !important;
            max-height: none !important;
            overflow: visible !important;
            /* Do NOT reset body padding — templates rely on it for spacing */
            /* and for header elements with negative margins (ribbon headers) */
          }
          /* Allow page breaks inside large items to avoid huge blank gaps */
          .resume-item,
          .experience-item,
          .experience-block,
          .education-item,
          .education-block,
          .project-item,
          .project-block,
          .certification-item,
          .cert-block,
          .skill-category,
          .award-item,
          .language-item {
            page-break-inside: auto !important;
            break-inside: auto !important;
          }
          /* Keep section headers attached to following content */
          h2, h3 {
            page-break-after: avoid !important;
            break-after: avoid !important;
          }
          p, li {
            orphans: 2 !important;
            widows: 2 !important;
          }
        </style>
      `;
      rendered = rendered.replace('</head>', `${exportCSS}</head>`);

      setExportProgress(30);
      setExportStatus('Preparing export...');

      await new Promise(r => setTimeout(r, 300));
      setExportProgress(50);
      setExportStatus(`Generating ${format.toUpperCase()}...`);

      const fileName = generateFilename(resumeData);
      const result = format === 'pdf'
        ? await ServerExportService.generatePDF(rendered, fileName, user.uid)
        : await ServerExportService.generateDOCX(rendered, fileName, user.uid, filterResumeData(resumeData), fontFamily, colorTheme, selectedTemplate?.id || null);

      if (!result.success) {
        throw new Error(result.error || 'Export failed');
      }

      setExportProgress(80);
      setExportStatus('Download complete, saving...');

      await new Promise(r => setTimeout(r, 300));
      setExportProgress(90);
      setExportStatus('Saving to cloud...');

      // Save to Firestore
      let currentResumeId = resumeId || `resume_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      const htmlBlob = new Blob([rendered], { type: 'text/html' });
      const htmlFileName = `${fileName.replace(/\.(pdf|docx)$/i, '')}_${Date.now()}.html`;
      const htmlStorageRef = ref(storage, `users/${user.uid}/resumes/${htmlFileName}`);
      
      await uploadBytes(htmlStorageRef, htmlBlob);
      const htmlDownloadURL = await getDownloadURL(htmlStorageRef);

      const resumeRef = doc(db, 'users', user.uid, 'resumes', currentResumeId);
      await setDoc(resumeRef, {
        data: filteredData,
        templateId: selectedTemplate?.id || null,
        templateName: selectedTemplate?.name || 'Unknown',
        templateTier: selectedTemplate?.plan || selectedTemplate?.tier || 'free',
        templateHTML: templateHTML || '',
        templateColorTheme: colorTheme || '',
        selectedFont: fontFamily || 'Calibri',
        htmlFileName,
        htmlDownloadURL,
        lastExportFormat: format,
        lastFileSize: result?.size || 0,
        userId: user.uid,
        resumeName: fileName.replace(/\.(pdf|docx)$/i, ''),
        updatedAt: serverTimestamp(),
        createdAt: serverTimestamp()
      }, { merge: true });

      await updateDoc(userRef, {
        resumeDownloadsUsed: (userData.resumeDownloadsUsed || 0) + 1,
        lastDownloadAt: serverTimestamp(),
        [`${format}DownloadCount`]: (userData[`${format}DownloadCount`] || 0) + 1
      });

      // Save to HR Space collection if checkbox is checked
      if (saveToHRConsultancy) {
        try {
          await addDoc(collection(db, 'hrSpace'), {
            userId: user.uid,
            userName: user.displayName || resumeData?.fullName || 'Unknown',
            userEmail: user.email || '',
            resumeId: currentResumeId,
            resumeName: fileName.replace(/\.(pdf|docx)$/i, ''),
            templateId: selectedTemplate?.id || null,
            templateName: selectedTemplate?.name || 'Unknown',
            htmlFileName,
            htmlDownloadURL,
            lastExportFormat: format,
            createdAt: serverTimestamp()
          });
        } catch (hrErr) {
          console.error('Failed to save to HR Space:', hrErr);
        }
      }

      setExportProgress(100);
      setExportStatus('Complete!');

      setTimeout(() => {
        setShowExportDialog(false);
        setExportProgress(0);
        setExportStatus('');
        setShowFeedbackModal(true);
      }, 1000);

    } catch (error) {
      console.error(`Export error:`, error);
      setExportStatus('Export failed');
      alert(`Export Failed\n\n${error.message}`);
      setTimeout(() => {
        setShowExportDialog(false);
        setExportProgress(0);
        setExportStatus('');
      }, 2000);
    }
  };

  const renderPreviewContent = () => (
    <div className="rev2-form-content">
      <div className="rev2-form-section">
        {/* Success banner - clean white style */}
        <div className="rev2-ready-banner">
          <div className="rev2-ready-title-row">
            <svg className="rev2-ready-tick" viewBox="0 0 20 20" fill="none">
              <circle cx="10" cy="10" r="9" stroke="#10b981" strokeWidth="1.8"/>
              <path d="M6 10.5l2.5 2.5 5-5" stroke="#10b981" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            <h3>Your resume is ready!</h3>
          </div>
          <p className="rev2-ready-subtitle">Server-side export &nbsp;·&nbsp; High quality &nbsp;·&nbsp; Only filled sections included</p>
        </div>

        {/* HR Consultancy Checkbox — controlled by VITE_SHOW_HR_CONSULTANCY env flag */}
        {import.meta.env.VITE_SHOW_HR_CONSULTANCY === 'true' && (
          <label className="rev2-hr-consultancy-checkbox">
            <input
              type="checkbox"
              checked={saveToHRConsultancy}
              onChange={(e) => setSaveToHRConsultancy(e.target.checked)}
            />
            <span className="rev2-hr-checkmark"></span>
            <span className="rev2-hr-label">Save Resume to HR Consultancy</span>
          </label>
        )}

        <div className="rev2-preview-actions">
          <button
            onClick={() => handleServerExport('pdf')}
            className="rev2-export-btn"
          >
            <svg viewBox="0 0 24 24" fill="none" className="rev2-export-file-icon">
              <path d="M13 2H6C5.46957 2 4.96086 2.21071 4.58579 2.58579C4.21071 2.96086 4 3.46957 4 4V20C4 20.5304 4.21071 21.0391 4.58579 21.4142C4.96086 21.7893 5.46957 22 6 22H18C18.5304 22 19.0391 21.7893 19.4142 21.4142C19.7893 21.0391 20 20.5304 20 20V9L13 2Z"
                fill="#DC2626" stroke="#B91C1C" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M13 2V9H20" stroke="#FEE2E2" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
              <text x="12" y="17" textAnchor="middle" fill="white" fontSize="5" fontWeight="bold">PDF</text>
            </svg>
            Download PDF
          </button>
          <button
            onClick={() => handleServerExport('docx')}
            className="rev2-export-btn"
          >
            <svg viewBox="0 0 24 24" fill="none" className="rev2-export-file-icon">
              <path d="M13 2H6C5.46957 2 4.96086 2.21071 4.58579 2.58579C4.21071 2.96086 4 3.46957 4 4V20C4 20.5304 4.21071 21.0391 4.58579 21.4142C4.96086 21.7893 5.46957 22 6 22H18C18.5304 22 19.0391 21.7893 19.4142 21.4142C19.7893 21.0391 20 20.5304 20 20V9L13 2Z"
                fill="#2563EB" stroke="#1E40AF" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M13 2V9H20" stroke="#DBEAFE" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
              <text x="12" y="17.5" textAnchor="middle" fill="white" fontSize="4" fontWeight="bold">DOCX</text>
            </svg>
            Download DOCX
          </button>
        </div>
        <p style={{ marginTop: '14px', fontSize: '12px', color: '#92400e', background: '#fffbeb', border: '1px solid #fde68a', borderRadius: '8px', padding: '10px 12px', textAlign: 'center', lineHeight: '1.5' }}>
          ⚠️ <strong>Note:</strong> DOCX formatting and alignment may vary from the live preview and PDF. For the most consistent layout, we recommend downloading your resume in <strong>PDF format</strong>.
        </p>
      </div>
    </div>
  );

  return (
    <div className={`rev2-container ${mobileView === 'preview' ? 'rev2-mobile-preview-active' : ''}`}>
      {/* Mobile Top Steps Bar */}
      <div className="rev2-mobile-steps-bar">
        <div className="rev2-mobile-steps-scroll">
          {steps.map((step, index) => (
            <button
              key={step.id}
              className={`rev2-mobile-step ${currentStep === index ? 'active' : ''} ${index < currentStep ? 'completed' : ''}`}
              onClick={() => { handleStepChange(index); setMobileView('editor'); }}
            >
              <span className="rev2-mobile-step-icon">{step.icon}</span>
              <span className="rev2-mobile-step-label">{step.label}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Sidebar */}
      <motion.div
        className={`rev2-sidebar ${sidebarExpanded ? 'expanded' : 'collapsed'}`}
        onMouseEnter={handleSidebarMouseEnter}
        onMouseLeave={handleSidebarMouseLeave}
        initial={{ width: 280 }}
        animate={{ width: sidebarExpanded ? 280 : 80 }}
        transition={{ duration: 0.3, ease: 'easeInOut' }}
      >
        <div className="rev2-sidebar-header">
          <motion.h2
            className="rev2-sidebar-title"
            animate={{ opacity: sidebarExpanded ? 1 : 0 }}
            transition={{ duration: 0.2 }}
          >
            Resume Steps
          </motion.h2>
        </div>

        <div className="rev2-sidebar-steps">
          {steps.map((step, index) => (
            <motion.button
              key={step.id}
              className={`rev2-step-item ${currentStep === index ? 'active' : ''} ${index < currentStep ? 'completed' : ''}`}
              onClick={() => handleStepChange(index)}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              style={{ '--step-color': step.color }}
            >
              <span className="rev2-step-number">{index + 1}</span>
              <span className="rev2-step-icon">{step.icon}</span>
              <AnimatePresence>
                {sidebarExpanded && (
                  <motion.span
                    className="rev2-step-label"
                    initial={{ opacity: 0, x: -10 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -10 }}
                    transition={{ duration: 0.2 }}
                  >
                    {step.label}
                  </motion.span>
                )}
              </AnimatePresence>
            </motion.button>
          ))}
        </div>

        {sidebarExpanded && (
          <motion.div
            className="rev2-sidebar-footer"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.2 }}
          >
            <div className="rev2-progress-info">
              <span className="rev2-progress-label">Progress</span>
              <span className="rev2-progress-value">{currentStep + 1}/{steps.length}</span>
            </div>
            <div className="rev2-progress-bar">
              <motion.div
                className="rev2-progress-fill"
                initial={{ width: 0 }}
                animate={{ width: `${((currentStep + 1) / steps.length) * 100}%` }}
                transition={{ duration: 0.5 }}
              />
            </div>
          </motion.div>
        )}
      </motion.div>

      {/* Editor Section */}
      <div className="rev2-editor-section">
        <div className="rev2-editor-header">
          <div className="rev2-header-left">
            <button className="rev2-back-btn" onClick={() => navigate('/templates')}>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M19 12H5M12 19l-7-7 7-7" />
              </svg>
              Back
            </button>
            <div className="rev2-header-info">
              <h1 className="rev2-editor-title">Create Your Resume</h1>
              <p className="rev2-editor-subtitle">Step {currentStep + 1} of {steps.length}</p>
            </div>
          </div>
          
          <div className="rev2-header-actions">
            <button 
              className="rev2-save-btn"
              onClick={handleSaveDraft}
              disabled={isSavingDraft}
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ width: '18px', height: '18px' }}>
                <path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z"/>
                <polyline points="17 21 17 13 7 13 7 21"/>
                <polyline points="7 3 7 8 15 8"/>
              </svg>
              {isSavingDraft ? 'Saving...' : 'Save Draft'}
            </button>
          </div>
        </div>

        <div className="rev2-editor-body">
          <AnimatePresence mode="wait">
            <motion.div
              key={currentStep}
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              transition={{ duration: 0.3 }}
            >
              {renderStepContent()}
            </motion.div>
          </AnimatePresence>

          <div className="rev2-editor-navigation">
            {currentStep > 0 && (
              <button className="rev2-nav-btn rev2-nav-prev" onClick={handlePrevious}>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M15 18l-6-6 6-6" />
                </svg>
                Previous
              </button>
            )}
            {currentStep < steps.length - 1 && (
              <button className="rev2-nav-btn rev2-nav-next" onClick={handleNext}>
                Next
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M9 18l6-6-6-6" />
                </svg>
              </button>
            )}
          </div>

          {/* Mobile Preview Toggle Button */}
          <div className="rev2-mobile-preview-toggle">
            <button className="rev2-mobile-toggle-btn" onClick={() => setMobileView('preview')}>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ width: '18px', height: '18px' }}>
                <rect x="2" y="3" width="20" height="14" rx="2" ry="2"/>
                <line x1="8" y1="21" x2="16" y2="21"/>
                <line x1="12" y1="17" x2="12" y2="21"/>
              </svg>
              View Live Preview
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ width: '16px', height: '16px' }}>
                <path d="M9 18l6-6-6-6" />
              </svg>
            </button>
            <p className="rev2-mobile-guide-text">
              Swipe steps above to navigate • Tap "Live Preview" to see your resume
            </p>
          </div>
        </div>
      </div>

      {/* Preview Section */}
      <div className="rev2-preview-section">
        <div className="rev2-preview-header">
          {/* Mobile Back to Editor button */}
          <button className="rev2-mobile-back-editor" onClick={() => setMobileView('editor')}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ width: '18px', height: '18px' }}>
              <path d="M19 12H5M12 19l-7-7 7-7" />
            </svg>
            Back to Editor
          </button>
          <h3 className="rev2-preview-title">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ width: '22px', height: '22px', marginRight: '8px' }}>
              <rect x="2" y="3" width="20" height="14" rx="2" ry="2"/>
              <line x1="8" y1="21" x2="16" y2="21"/>
              <line x1="12" y1="17" x2="12" y2="21"/>
            </svg>
            Live Preview
          </h3>
          
          <div className="rev2-preview-controls">
            <button 
              className={`rev2-theme-btn ${showSectionToggle ? 'active' : ''}`}
              onClick={() => { setShowSectionToggle(!showSectionToggle); setShowColorPicker(false); setShowFontPicker(false); }}
              title="Toggle Sections"
            >
              <span>📋</span>
            </button>
            <button 
              ref={colorBtnRef}
              className={`rev2-theme-btn ${!tierColorPolicy.enabled ? 'rev2-theme-btn-locked' : ''}`}
              onClick={handleColorPickerToggle}
              title={tierColorPolicy.enabled ? 'Change Color Theme' : tierColorPolicy.label}
            >
              <span>{tierColorPolicy.enabled ? '🎨' : '🔒'}</span>
            </button>
            <button 
              ref={fontBtnRef}
              className="rev2-theme-btn" 
              onClick={handleFontPickerToggle}
              title="Change Font"
            >
              <span>Aa</span>
            </button>
          </div>
        </div>

        {/* Section Visibility Toggle Panel */}
        {showSectionToggle && (
          <div className="rev2-section-toggle-panel" ref={sectionToggleRef}>
            <div className="rev2-section-toggle-header">
              <span>Show/Hide Sections</span>
              <button className="rev2-section-toggle-close" onClick={() => setShowSectionToggle(false)}>✕</button>
            </div>
            <div className="rev2-section-toggle-list">
              {[
                { key: 'summary', label: 'Professional Summary', icon: '📝' },
                { key: 'experience', label: 'Work Experience', icon: '💼' },
                { key: 'education', label: 'Education', icon: '🎓' },
                { key: 'skills', label: 'Skills', icon: '⚡' },
                { key: 'projects', label: 'Projects', icon: '🚀' },
                { key: 'certifications', label: 'Certifications', icon: '🏆' },
                { key: 'languages', label: 'Languages', icon: '🌐' },
                { key: 'achievements', label: 'Achievements', icon: '🏅' }
              ].map(section => (
                <label key={section.key} className="rev2-section-toggle-item">
                  <input
                    type="checkbox"
                    checked={!hiddenSections.includes(section.key)}
                    onChange={(e) => {
                      if (e.target.checked) {
                        setHiddenSections(prev => prev.filter(s => s !== section.key));
                      } else {
                        setHiddenSections(prev => [...prev, section.key]);
                      }
                    }}
                  />
                  <span className="rev2-section-toggle-icon">{section.icon}</span>
                  <span className="rev2-section-toggle-label">{section.label}</span>
                </label>
              ))}
            </div>
          </div>
        )}

        <ResumePreview
          html={previewHTML}
          loading={loading}
          colorVars={colorVars}
        />

        {/* Mobile Bottom Action Bar - Back to Editor */}
        <div className="rev2-mobile-preview-bottom">
          <button className="rev2-mobile-back-btn" onClick={() => setMobileView('editor')}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ width: '16px', height: '16px' }}>
              <path d="M19 12H5M12 19l-7-7 7-7" />
            </svg>
            Back to Editor
          </button>
        </div>
      </div>

      {/* Theme Dropdowns */}
      {showColorPicker && (
        <div 
          className="rev2-theme-dropdown"
          ref={colorDropdownRef}
          style={{ top: dropdownPosition.top, left: dropdownPosition.left }}
        >
          <div className="rev2-dropdown-header">
            <span className="rev2-dropdown-title">🎨 Color Themes</span>
            <button 
              className="rev2-dropdown-close"
              onClick={() => setShowColorPicker(false)}
            >
              ✕
            </button>
          </div>
          {!tierColorPolicy.enabled ? (
            <div style={{ padding: '12px 16px', color: '#888', fontSize: '13px', textAlign: 'center' }}>
              {tierColorPolicy.label}
            </div>
          ) : (
          <div className="rev2-color-grid">
            {Object.keys(colorPalettes).map((color) => (
              <button
                key={color}
                className={`rev2-color-swatch ${colorTheme === color ? 'active' : ''}`}
                onClick={() => handleColorChange(color)}
                title={color.charAt(0).toUpperCase() + color.slice(1)}
                style={{
                  background: `linear-gradient(135deg, ${swatchGradients[color]?.[0] || colorPalettes[color].primary}, ${swatchGradients[color]?.[1] || colorPalettes[color].primary})`
                }}
              >
                {colorTheme === color && <span className="rev2-check-icon">✓</span>}
              </button>
            ))}
          </div>
          )}
        </div>
      )}

      {showFontPicker && (
        <div 
          className="rev2-theme-dropdown"
          ref={fontDropdownRef}
          style={{ top: dropdownPosition.top, left: dropdownPosition.left }}
        >
          <div className="rev2-dropdown-header">
            <span className="rev2-dropdown-title">🔤 Font Families</span>
            <button 
              className="rev2-dropdown-close"
              onClick={() => setShowFontPicker(false)}
            >
              ✕
            </button>
          </div>
          <div
            className="rev2-font-list"
            ref={fontListRef}
            onKeyDown={handleFontKeyDown}
          >
            {fontFamilies.map((font, idx) => (
              <button
                key={font}
                className={`rev2-font-option ${fontFamily === font ? 'active' : ''}`}
                onClick={() => handleFontChange(font)}
                onFocus={() => setFontFocusIdx(idx)}
                style={{ fontFamily: font }}
                tabIndex={0}
              >
                <span className="rev2-font-name">{font}</span>
                {fontFamily === font && <span className="rev2-check-icon">✓</span>}
              </button>
            ))}
          </div>
        </div>
      )}

    {/* Export Progress Dialog */}
    {showExportDialog && (
      <div className="export-dialog-overlay" style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 10000
      }}>
        <div className="export-dialog" style={{
          background: 'white',
          borderRadius: '16px',
          padding: '32px',
          maxWidth: '400px',
          width: '90%',
          boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)'
        }}>
          <div style={{ textAlign: 'center' }}>
            <div style={{
              width: '64px',
              height: '64px',
              margin: '0 auto 24px',
              borderRadius: '50%',
              background: 'linear-gradient(135deg, #1e293b 0%, #0f172a 100%)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              animation: exportProgress === 100 ? 'none' : 'pulse 2s infinite'
            }}>
              {exportProgress === 100 ? (
                <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3">
                  <polyline points="20 6 9 17 4 12"></polyline>
                </svg>
              ) : (
                <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" style={{ animation: 'spin 2s linear infinite' }}>
                  <circle cx="12" cy="12" r="10" strokeDasharray="60" strokeDashoffset="15"></circle>
                </svg>
              )}
            </div>

            <h3 style={{
              margin: '0 0 12px 0',
              fontSize: '24px',
              fontWeight: '600',
              color: '#1a1a1a'
            }}>
              {exportProgress === 100 ? 'Export Complete!' : 'Exporting Resume'}
            </h3>

            <p style={{
              margin: '0 0 24px 0',
              color: '#666',
              fontSize: '14px'
            }}>
              {exportStatus}
            </p>

            <div style={{
              width: '100%',
              height: '8px',
              background: '#f0f0f0',
              borderRadius: '4px',
              overflow: 'hidden',
              marginBottom: '12px'
            }}>
              <div style={{
                width: `${exportProgress}%`,
                height: '100%',
                background: 'linear-gradient(90deg, #1e293b 0%, #0f172a 100%)',
                transition: 'width 0.6s cubic-bezier(0.4, 0, 0.2, 1)',
                borderRadius: '4px'
              }}></div>
            </div>

            <p style={{
              margin: 0,
              fontSize: '14px',
              fontWeight: '600',
              color: '#1e293b'
            }}>
              {exportProgress}%
            </p>
          </div>
        </div>
      </div>
    )}

    {/* Feedback Modal */}
    <FeedbackModal 
      show={showFeedbackModal}
      onClose={() => setShowFeedbackModal(false)}
    />

    {/* Photo Upload Modal */}
    <PhotoUploadModal
      isOpen={showPhotoModal}
      onClose={() => setShowPhotoModal(false)}
      onCropComplete={handlePhotoUpload}
    />

    {/* Limit Exceeded Modal - Add-On Purchase */}
    <LimitExceededModal
      isOpen={showAddonModal}
      onClose={() => setShowAddonModal(false)}
      limitType="download"
      templateTier={selectedTemplate?.plan || selectedTemplate?.tier || ''}
      resumeState={{ resumeData, templateHTML, selectedTemplate, colorTheme, fontFamily, hiddenSections, resumeId }}
      onBuyAddon={async (addonData) => {
        const success = await purchaseAddon({
          addonData,
          limitType: 'download',
          templateId: selectedTemplate?.id || '',
          templateTier: selectedTemplate?.plan || selectedTemplate?.tier || '',
          resumeState: { resumeData, templateHTML, selectedTemplate, colorTheme, fontFamily, hiddenSections, resumeId }
        });
        if (success) {
          setShowAddonModal(false);
          // Retry download after successful addon purchase
          setTimeout(() => handleServerExport('pdf'), 500);
        }
      }}
    />
  </div>
  );
};

export default ResumeEditorV2;
